import LayeredAutocompleteSelect from "./LayeredAutocompleteSelect";
import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton as MuiIconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
  Chip,
} from "@mui/material";
import AssistantIcon from "../assets/img/assist_avatar.svg";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloseIconMui from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { useAppDispatch, useAppSelector } from "../hooks/storeHook";
import { ChatAction } from "../actions";
import { ActionTypes } from "../store/actionTypes";
import SupervisorAction from "../actions/supervisorAction";
import { store } from "../store";
import ChatApi from "../apis/chatApi";
import {
  inferInformationRequestFieldType,
  validateField,
} from "../utils/validationUtils";
import { getChatSendErrorMessage } from "../utils/apiErrorMessage";
import { dispatchChatSendFailure } from "../utils/chatSendFailure";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { getFontSizes } from '../utils/fontSizeUtils';
import { isHitlApprovalAction } from "../shared/hitl/hitlUtils";
import { hitlUsesBrowser } from "../shared/hitl/computerUseTools";
import { isActionPauseReview } from "../shared/hitl/actionPause";
import PauseCard from "../shared/components/PauseCard";
import TechnicianChecklistReview from "../shared/components/TechnicianChecklistReview";
import WorkflowStepResolutionReview from "../shared/components/WorkflowStepResolutionReview";
import { parseNestedArgs } from "../shared/utils/argumentUtils";
import { extractHumanReviewPayloadFromContent } from "../shared/utils/messageUtils";
import {
  getPdfSubtitleBeforeTable,
  messageBlocksTablePdfDownloads,
  mountTablePdfDownloadLinksBelowTables,
  serializeTableCellForPdfDownload,
  TABLE_PDF_DEFAULT_DOCUMENT_TITLE,
  setTablePdfLinkLoadingState,
} from "../shared/utils/tablePdfPayload";
import {
  downloadThreadCsvExport,
  downloadThreadPdfExport,
  isCsvExportHref,
  isPdfExportHref,
  parseCsvExportSearchParams,
  parsePdfExportSearchParams,
} from "../shared/utils/csvExportDownload";
import HitlActionParamsEditor from "../shared/components/HitlActionParamsEditor";
import { NEUTRAL, RADIUS, VIOLET } from "../styles/brandTokens";
import {
  CARD_BACKGROUND,
  CARD_EDGE,
  approvalLinkButtonSx,
  approvalPrimaryButtonSx,
  approvalSecondaryButtonSx,
} from "./approvalCard/approvalStyles";
import { HitlUpdatedFieldsChip } from "../shared/components/HitlUpdatedFieldFrame";
import { normalizeUpdatedArgPaths } from "../shared/hitl/hitlArgDiff";
import {
  buildMailtoFromSendEmailInput,
  buildOutlookWebComposeFromSendEmailInput,
  mergeM365SendEmailInput,
} from "../utils/mailtoUtils";
import { formatHitlToolDescription } from "../utils/hitlToolDescription";
import { getWebAuthToken } from "../utils/authTokenStorage";
import ScheduleApi, { TheoSchedule } from "../apis/scheduleApi";
import WorkflowTimerScheduleActions from "../shared/components/WorkflowTimerScheduleActions";
import { containsScheduleReference } from "../shared/utils/scheduleRefs";
import { formatHumanReviewAck } from "../shared/utils/messageSenderLabels";
import { isScheduleActionLdEnabled, isSchedulerLdEnabled } from "../utils/schedulerAccess";
import {
  formatDateTimeInTimeZone,
  fromDateTimeLocalInTimeZone,
  getDefaultTimeZone,
  getTimeZoneOptions,
  toDateTimeLocalInTimeZone,
} from "../shared/utils/timezoneUtils";

// Module-level set to track resolved threads across all ChatBubble instances
const resolvedThreads = new Set<string>();

function getScheduledScheduleId(humanReview?: { schedule_id?: string | number | null } | null) {
  const id = humanReview?.schedule_id;
  return id === null || id === undefined || id === "" ? null : String(id);
}

function contentText(content: React.ReactNode): string {
  if (content === null || content === undefined || typeof content === "boolean") return "";
  if (typeof content === "string" || typeof content === "number") return String(content);
  if (Array.isArray(content)) return content.map(contentText).join(" ");
  if (React.isValidElement<any>(content)) {
    const html = content.props?.dangerouslySetInnerHTML?.__html;
    const children = content.props?.children;
    return [typeof html === "string" ? html : "", contentText(children)].filter(Boolean).join(" ");
  }
  return "";
}

function extractWorkflowTimerScheduleId(content: React.ReactNode) {
  const text = contentText(content);
  if (!/Workflow paused/i.test(text)) return null;
  const match = text.match(/\/schedules\?[^"'<>\s]*scheduleId=([^"'&<>\s]+)/i);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function contentHasScheduleAnchor(content: React.ReactNode) {
  const text = contentText(content);
  return /\/schedules\?[^"'<>\s]*scheduleId=/i.test(text) && /<a[\s>]/i.test(text);
}

function getHumanReviewCallIds(humanReview?: { call_id?: string | string[] | null } | null) {
  const callIds = humanReview?.call_id;
  return (Array.isArray(callIds) ? callIds : callIds ? [callIds] : []).filter(Boolean).map(String);
}

function parseArgsJson(value: unknown): Record<string, any> {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value as Record<string, any>;
  if (typeof value !== "string") return {};

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function getToolArgs(tool: any, editedArgs: Record<number, Record<string, any>>, index: number) {
  const edited = editedArgs[index];
  return edited && Object.keys(edited).length > 0 ? edited : parseArgsJson(tool?.args_json);
}

function scheduleStatusColor(status?: string | null) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "succeeded" || normalized === "completed") return "success";
  if (normalized === "failed") return "error";
  if (normalized === "running") return "warning";
  if (normalized === "canceled") return "default";
  return "info";
}

function normalizeScheduledActionStatus(status?: string | null) {
  const normalized = String(status || "").toLowerCase();
  if (["succeeded", "failed", "running", "canceled", "pending"].includes(normalized)) {
    return normalized;
  }
  return status || "pending";
}

/** Pause-message status chip. Pending waits show clock + X only; failed still shows a chip. */
function getWorkflowTimerStatusDisplay(schedule?: TheoSchedule | null) {
  const scheduleStatus = String(schedule?.status || "").toLowerCase();
  if (scheduleStatus === "failed") {
    return { label: "schedule failed", color: "error" as const };
  }
  return null;
}

function getScheduleActionByCallId(schedule?: TheoSchedule | null) {
  const map = new Map<string, any>();
  (schedule?.actions || []).forEach((action: any) => {
    if (action?.source_call_id) {
      map.set(String(action.source_call_id), action);
    }
  });
  return map;
}

const messageContentWrapSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  whiteSpace: "normal",
  "& *": {
    minWidth: 0,
    maxWidth: "100%",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& a": {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& pre, & code": {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& table": {
    width: "100%",
    maxWidth: "100%",
    tableLayout: "fixed",
  },
  "& th, & td": {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& img, & video, & iframe": {
    maxWidth: "100%",
    height: "auto",
  },
  "& .neo-chat-attachment": {
    minWidth: 0,
    maxWidth: "100%",
  },
  "& .neo-chat-attachment span:last-of-type": {
    minWidth: 0,
    whiteSpace: "normal !important",
    overflow: "visible !important",
    textOverflow: "clip !important",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
} as const;

const parseExportParams = (raw: string): { actionName: string; paramsObj: Record<string, unknown> } | null => {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length < 2) return null;
    const actionName = typeof parsed[0] === "string" ? parsed[0].trim() : "";
    if (!actionName) return null;
    const rawParams = parsed[1];
    const paramsObj =
      rawParams && typeof rawParams === "object" && !Array.isArray(rawParams)
        ? (rawParams as Record<string, unknown>)
        : {};
    return { actionName, paramsObj };
  } catch {
    return null;
  }
};


interface ChatBubbleProps {
  sender: "assistant" | "user";
  content: React.ReactNode;
  timestamp: string;
  senderName: string;
  userAvatar?: string;
  userId: string;
  human_review?: {
    call_id?: string | string[] | null;
    tools?: Array<{
      tool_name?: string;
      tool_description?: string;
      args_json?: string | object;
      original_args_json?: Record<string, unknown>;
      updated_arg_paths?: string[];
      resolved_args?: any;
      call_id?: string;
      arg_types_json?: any;
      action?: string | null;
      is_undoable?: boolean;
    }>;
    plans?: Array<{
      plan_name?: string;
      plan_description?: string;
      steps?: any[];
      url?: string;
      action?: string | null;
    }>;
    information_request?: Array<{
      id?: string;
      question?: string;
      form_type?: string;
      suggested_answer?: any;
      card_json?: string;
    }>;
    steps_taken?: string[];
    tool_action?: string | string[] | null; // CANCEL/CONTINUE/UPDATE/null for each tool
    selected_plan_name?: string | null;
    plan_id?: string | string[] | null;
    schedule_id?: string | number | null;
    schedule_title?: string | null;
    information_answers?: Record<string, string>;
  };
  isHistory?: boolean;
  isLastMessage?: boolean;
  human_review_status?: string;
  human_review_response?: string;
  onReviewAction?: (
    action: "CONTINUE" | "CANCEL" | "UPDATE" | "SCHEDULED",
    args?: string,
    messageId?: string
  ) => void | Promise<void>;
  /** Optional parent snackbar/toast when HITL submit fails. */
  onReviewError?: (message: string) => void;
  messageId?: string;
  message?: any; // Add message prop to access sent arguments
  currentUser?: any;
  aiThreadId?: string | number | null;
  customerId?: string | number | null;
  pending?: boolean; // Add pending prop for optimistic updates
  status?: 'pending' | 'sent' | 'confirmed' | 'failed'; // Add status prop
  ticketId?: string | null; // Add ticketId prop
  isEmbedded?: boolean; // When true, adjusts font sizes for ConnectWise pod embed
  plan_progress?: {
    plan_description?: string;
    completed_step_indices: number[];
    total_steps: number;
  };
  headerInlineContent?: React.ReactNode;
  automationModeBadge?: React.ReactNode;
  /** Chat surfaces only. When provided, shows an inline Undo button on approved HITL bubbles. */
  onOpenUndoModal?: (preSelectedCallIds: string[]) => void;
  /** Called after a paused-workflow wait is canceled so the ticket schedule list can refresh. */
  onWorkflowTimerCanceled?: (scheduleId: string) => void;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  sender,
  content,
  timestamp,
  senderName,
  userAvatar,
  userId,
  human_review,
  isHistory = false,
  isEmbedded = false,
  human_review_status,
  human_review_response,
  isLastMessage = false,
  onReviewAction,
  onReviewError,
  messageId,
  message,
  currentUser: propCurrentUser,
  aiThreadId: propAiThreadId,
  customerId: propCustomerId,
  pending = false,
  status,
  ticketId: propTicketId,
  plan_progress,
  headerInlineContent,
  automationModeBadge,
  onOpenUndoModal,
  onWorkflowTimerCanceled,
}) => {
  // Calculate font sizes based on embedded state
  const fontSizes = getFontSizes(isEmbedded);
  
  const [modalOpen, setModalOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isTruncatable, setIsTruncatable] = useState(false);
  const [reviewed, setReviewed] = useState(
    human_review_status === "approved" || 
    human_review_status === "canceled" ||
    human_review_status === "scheduled"
  );
  const [reviewAction, setReviewAction] = useState<"CONTINUE" | "CANCEL" | "UPDATE" | "SCHEDULED" | null>(
    human_review_status === "approved"
      ? "CONTINUE"
      : human_review_status === "canceled"
      ? "CANCEL"
      : human_review_status === "scheduled"
      ? "SCHEDULED"
      : null
  );
  const [expandedTools, setExpandedTools] = useState<Set<number>>(new Set());
  const [selectedTools, setSelectedTools] = useState<Set<number>>(new Set());
  const [selectedPlans, setSelectedPlans] = useState<Set<number>>(new Set());
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<"existing" | "new">("new");
  const [availableSchedules, setAvailableSchedules] = useState<TheoSchedule[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>("");
  const [scheduleTitle, setScheduleTitle] = useState("");
  const [scheduleDescription, setScheduleDescription] = useState("");
  const [scheduleTimezone, setScheduleTimezone] = useState(() => getDefaultTimeZone());
  const [scheduleFor, setScheduleFor] = useState(() => toDateTimeLocalInTimeZone(null, getDefaultTimeZone()));
  const [scheduledScheduleId, setScheduledScheduleId] = useState<string | null>(() => getScheduledScheduleId(human_review as any));
  const [linkedSchedule, setLinkedSchedule] = useState<TheoSchedule | null>(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [cancelingWorkflowTimer, setCancelingWorkflowTimer] = useState(false);
  const scheduleTimezoneOptions = React.useMemo(() => getTimeZoneOptions(scheduleTimezone), [scheduleTimezone]);
  const humanReviewScheduleId = getScheduledScheduleId(human_review as any);
  const workflowTimerScheduleId = React.useMemo(() => extractWorkflowTimerScheduleId(content), [content]);
  const workflowTimerContentHasScheduleLink = React.useMemo(() => contentHasScheduleAnchor(content), [content]);
  const linkedScheduleIdForStatus = scheduledScheduleId || humanReviewScheduleId || workflowTimerScheduleId;
  const humanReviewCallIdsKey = getHumanReviewCallIds(human_review as any).join("\u0001");
  const hasUserInteracted = useRef(false);

  // Feature flag for Steps Taken section
  const flags = useFeatureFlag();
  const showStepsTaken = flags['show-steps-taken'] ?? false;
  const schedulerEnabled = isSchedulerLdEnabled(flags as Record<string, unknown>);
  const scheduleActionEnabled = schedulerEnabled && isScheduleActionLdEnabled(flags as Record<string, unknown>);
  const scheduleHref = schedulerEnabled && linkedScheduleIdForStatus
    ? `/schedules?scheduleId=${encodeURIComponent(linkedScheduleIdForStatus)}`
    : null;

  // Helper functions to check HITL type
  const hasTools = human_review?.tools && Array.isArray(human_review.tools) && human_review.tools.length > 0;
  const hasPlans = human_review?.plans && Array.isArray(human_review.plans) && human_review.plans.length > 0;
  const hasInformationRequests = human_review?.information_request && 
    Array.isArray(human_review.information_request) && 
    human_review.information_request.length > 0;
  const shouldRenderMessageContent = React.useMemo(() => {
    if (content === null || content === undefined || content === "") return false;
    if (human_review && extractHumanReviewPayloadFromContent(content)) {
      return false;
    }
    if (typeof content === "string") {
      return content.trim().length > 0;
    }
    return true;
  }, [content, human_review]);
  
  // Check if any information request has form_type "workflow"
  const isWorkflowFormType = hasInformationRequests && 
    human_review?.information_request?.some((req: any) => req.form_type === "workflow");
  const isTechnicianChecklistFormType = hasInformationRequests &&
    human_review?.information_request?.every((req: any) => req.form_type === "technician_checklist");
  const isWorkflowResolutionFormType = hasInformationRequests &&
    human_review?.information_request?.every((req: any) => req.form_type === "structured_workflow_resolution");
  // An action pause (sign-in, authorization wait, a field to fill, a background-job wait): the
  // whole card is described by `card_json` and rendered by PauseCard, whose button is the answer.
  const isActionPause = Boolean(hasInformationRequests && isActionPauseReview(human_review));


  // Create a stable key from human_review fields that affect state
  const humanReviewKey = human_review ? JSON.stringify({
    tool_action: human_review.tool_action,
    tools: human_review.tools,
    selected_plan_name: (human_review as any)?.selected_plan_name,
    plans: human_review.plans,
    information_answers: human_review.information_answers,
    information_request: human_review.information_request,
  }) : null;

  useEffect(() => {
    if (!schedulerEnabled) return;
    if (humanReviewScheduleId) {
      setScheduledScheduleId(humanReviewScheduleId);
    }
  }, [humanReviewScheduleId, schedulerEnabled]);

  useEffect(() => {
    if (!schedulerEnabled) return;
    if (scheduledScheduleId || human_review_status !== "scheduled") return;
    const callIds = new Set(humanReviewCallIdsKey ? humanReviewCallIdsKey.split("\u0001") : []);
    if (callIds.size === 0) return;
    let canceled = false;
    const scheduleApi = new ScheduleApi();
    scheduleApi.listSchedules()
      .then((response) => {
        if (canceled) return;
        const schedule = (response.schedules || []).find((item) =>
          (item.actions || []).some((action) => action.source_call_id && callIds.has(String(action.source_call_id)))
        );
        if (schedule?.id) {
          setScheduledScheduleId(String(schedule.id));
        }
      })
      .catch(() => {});
    return () => {
      canceled = true;
    };
  }, [scheduledScheduleId, human_review_status, humanReviewCallIdsKey, schedulerEnabled]);

  useEffect(() => {
    if (!schedulerEnabled) {
      setLinkedSchedule(null);
      return;
    }
    if (!linkedScheduleIdForStatus) {
      setLinkedSchedule(null);
      return;
    }

    let canceled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const scheduleApi = new ScheduleApi();

    const loadSchedule = async () => {
      try {
        const response = await scheduleApi.getSchedule(linkedScheduleIdForStatus);
        if (canceled) return;
        const schedule = response.schedule || null;
        setLinkedSchedule(schedule);
        const scheduleStatus = String(schedule?.status || "").toLowerCase();
        const hasActiveAction = (schedule?.actions || []).some((action: any) =>
          ["pending", "running"].includes(String(action?.status || "").toLowerCase())
        );
        if (scheduleStatus === "scheduled" || scheduleStatus === "running" || hasActiveAction) {
          timeoutId = setTimeout(loadSchedule, 30000);
        }
      } catch {
        if (!canceled) {
          setLinkedSchedule(null);
        }
      }
    };

    loadSchedule();

    return () => {
      canceled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [linkedScheduleIdForStatus, schedulerEnabled]);

  const scheduledActionByCallId = React.useMemo(() => getScheduleActionByCallId(linkedSchedule), [linkedSchedule]);
  const workflowTimerScheduleStatus = React.useMemo(
    () => (workflowTimerScheduleId ? getWorkflowTimerStatusDisplay(linkedSchedule) : null),
    [linkedSchedule, workflowTimerScheduleId]
  );
  const scheduleViewIconButtonSx = React.useMemo(() => ({
    width: isEmbedded ? 30 : 34,
    height: isEmbedded ? 30 : 34,
    p: 0,
    ml: 1,
    border: "1px solid #8F6ED5",
    borderRadius: "6px",
    color: "#6D55B8",
    backgroundColor: "#FFFFFF",
    flexShrink: 0,
    "&:hover": {
      borderColor: "#7054C8",
      backgroundColor: "rgba(160,132,232,0.08)",
    },
  }), [isEmbedded]);
  const renderScheduleViewIconLink = () => {
    if (!scheduleHref) return null;
    return (
      <Tooltip title="Open schedule" arrow>
        <MuiIconButton
          component="a"
          href={scheduleHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open schedule"
          size="small"
          onClick={(event) => event.stopPropagation()}
          sx={scheduleViewIconButtonSx}
        >
          <ScheduleIcon sx={{ fontSize: isEmbedded ? 16 : 18 }} />
        </MuiIconButton>
      </Tooltip>
    );
  };

  const workflowTimerCanCancel = React.useMemo(() => {
    if (!workflowTimerScheduleId || !schedulerEnabled) return false;
    const status = String(linkedSchedule?.status || "scheduled").toLowerCase();
    return ["scheduled", "running"].includes(status);
  }, [linkedSchedule?.status, schedulerEnabled, workflowTimerScheduleId]);

  const handleCancelWorkflowTimer = async () => {
    if (!workflowTimerScheduleId || cancelingWorkflowTimer) return;
    setCancelingWorkflowTimer(true);
    dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
    dispatch({ type: ActionTypes.START_THINKING });
    try {
      await new ScheduleApi().cancelSchedule(workflowTimerScheduleId);
      onWorkflowTimerCanceled?.(String(workflowTimerScheduleId));
    } catch {
      dispatch({ type: ActionTypes.END_THINKING });
      dispatch({ type: ActionTypes.CHAT_LOADING, payload: false });
    } finally {
      setCancelingWorkflowTimer(false);
    }
  };

  const renderWorkflowTimerScheduleActions = () => {
    if (!workflowTimerScheduleId || !schedulerEnabled) return null;
    return (
      <WorkflowTimerScheduleActions
        scheduleHref={scheduleHref}
        showScheduleLink={!workflowTimerContentHasScheduleLink}
        canCancel={workflowTimerCanCancel}
        canceling={cancelingWorkflowTimer}
        compact={isEmbedded}
        onCancel={handleCancelWorkflowTimer}
      />
    );
  };

  // Unified HITL state initialization - runs when human_review key fields change
  useEffect(() => {
    if (!human_review) {
      setSelectedTools(new Set());
      setSelectedPlans(new Set());
      setInformationAnswers({});
      return;
    }

    // Initialize tools state
    if (hasTools && human_review.tools) {
      // If tool_action exists, always sync selectedTools with it (server is source of truth)
      const toolActions = Array.isArray(human_review.tool_action)
        ? human_review.tool_action
        : human_review.tool_action
        ? [human_review.tool_action]
        : [];
      const hasToolActions = toolActions.some(
        (action) => typeof action === "string" && action.trim().length > 0
      );
      if (hasToolActions) {
        const initialSelected = new Set<number>();
        toolActions.forEach((action, index) => {
          if (action === "CONTINUE" || action === "UPDATE" || action === "SCHEDULED") {
            initialSelected.add(index);
          }
        });
        setSelectedTools(initialSelected);
      } else if (!hasUserInteracted.current) {
        // Default: select all tools if no tool_action (pending HITL)
        setSelectedTools(new Set(human_review.tools.map((_, i) => i)));
      }
    } else {
      setSelectedTools(new Set());
    }

    // Initialize plans state
    if (hasPlans && human_review.plans) {
      // If selected_plan_name exists, always sync selectedPlans with it (server is source of truth)
      const selectedPlanName = (human_review as any)?.selected_plan_name;
      if (selectedPlanName) {
        const selectedIndex = human_review.plans.findIndex((p: any) => p.plan_name === selectedPlanName);
        if (selectedIndex !== -1) {
          setSelectedPlans(new Set([selectedIndex]));
        }
      } else if (!hasUserInteracted.current) {
        // Default pending HITL behavior:
        // - one plan: preselect it
        // - multiple plans: require explicit user choice (select none)
        if (human_review.plans.length === 1) {
          setSelectedPlans(new Set([0]));
        } else {
          setSelectedPlans(new Set());
        }
      }
    } else {
      setSelectedPlans(new Set());
    }

    // Initialize information answers state
    // Always sync with information_answers when it exists (server is source of truth)
    if (hasInformationRequests && human_review.information_request) {
      if (human_review.information_answers) {
        const updatedAnswers: Record<string, string> = {};
        human_review.information_request.forEach((req: any) => {
          updatedAnswers[req.id] = (human_review.information_answers as any)?.[req.id] || "";
        });
        setInformationAnswers(updatedAnswers);
      } else {
        // Pre-populate from suggested_answer if available
        const initialAnswers: Record<string, string> = {};
        human_review.information_request.forEach((req: any) => {
          // Extract suggested_answer value
          let suggestedValue = "";
          if (req.suggested_answer) {
            // Handle both string and object (StringValue wrapper) cases
            if (typeof req.suggested_answer === 'string') {
              suggestedValue = req.suggested_answer.trim();
            } else if (req.suggested_answer.value && typeof req.suggested_answer.value === 'string') {
              suggestedValue = req.suggested_answer.value.trim();
            } else if (typeof req.suggested_answer === 'object' && req.suggested_answer !== null) {
              // Try to extract value from object
              suggestedValue = String(req.suggested_answer).trim();
            }
          }
          
          // Use suggested_answer if available, otherwise empty string
          initialAnswers[req.id] = suggestedValue || "";
        });
        setInformationAnswers(initialAnswers);
      }
    } else {
      setInformationAnswers({});
    }
    // humanReviewKey already captures all human_review changes, and hasInformationRequests/hasPlans/hasTools are derived from it
    // selectedPlans.size is state being set inside this effect, not a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [humanReviewKey]);

  const [stepsTaken, setStepsTaken] = useState<string[]>([]);

  // Feedback states
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'thumbs_up' | 'thumbs_down' | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [hitlPdfDownloading, setHitlPdfDownloading] = useState(false);
  // Information requests state
  const [informationAnswers, setInformationAnswers] = useState<Record<string, string>>(() => {
    if (human_review?.information_request) {
      const answers: Record<string, string> = {};
      human_review.information_request.forEach((req: any) => {
        answers[req.id] = (human_review as any)?.information_answers?.[req.id] || "";
      });
      return answers;
    }
    return {};
  });


  const [postingAllIR, setPostingAllIR] = useState<boolean>(false);
  const [postedAllIR, setPostedAllIR] = useState<boolean>(false);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

  // Update form-level errors whenever validation errors change
  useEffect(() => {
    if (!human_review?.information_request) return;
    
    const errors: string[] = [];
    human_review.information_request.forEach((req: any) => {
      if (validationErrors[req.id] && touchedFields[req.id]) {
        const fieldName = req.question.split(':')[0].trim();
        errors.push(`${fieldName}: ${validationErrors[req.id]}`);
      }
    });
    setFormErrors(errors);
  }, [validationErrors, touchedFields, human_review?.information_request]);

  const dispatch = useAppDispatch();
  const reduxAiThreadId = useAppSelector((state) => state.chat.aiThreadId);
  const aiThreadId =
    propAiThreadId !== undefined && propAiThreadId !== null && String(propAiThreadId).trim()
      ? String(propAiThreadId)
      : reduxAiThreadId;
  const reduxTicketId = useAppSelector((state) => state.chat.ticketId);
  const ticketId = propTicketId ?? reduxTicketId;
  const messages = useAppSelector((state) => state.chat.messages);
  const reduxCurrentUser = useAppSelector((state) => state.auth.user);
  const currentUser = propCurrentUser ?? reduxCurrentUser;

  useEffect(() => {
    if (human_review?.information_request && Array.isArray(human_review.information_request)) {
      const hasAnswers = !!(human_review.information_answers && Object.keys(human_review.information_answers).length > 0);
      const isReviewed = human_review_status === "approved" || human_review_status === "canceled" || human_review_status === "scheduled";
      if (!hasAnswers && !isReviewed) {
        setPostedAllIR(false);
        setPostingAllIR(false);
      }
    }
  }, [human_review?.information_request, human_review?.information_answers, human_review_status, messageId]);

  useEffect(() => {
    const run = async () => {
      if (!aiThreadId) {
        return;
      }
      const threadIdStr = String(aiThreadId);
      
      const hasPendingIR = !!(
        human_review?.information_request &&
        Array.isArray(human_review.information_request) &&
        human_review.information_request.length > 0 &&
        (!human_review.information_answers || Object.keys(human_review.information_answers).length === 0) &&
        human_review_status !== "approved" &&
        human_review_status !== "canceled" &&
        human_review_status !== "scheduled"
      );
      
      if (!hasPendingIR) {
        return;
      }
      
      if (resolvedThreads.has(threadIdStr)) {
        return;
      }
      resolvedThreads.add(threadIdStr);

      try {
        const api = new ChatApi();
        await api.resolveInformationRequests(threadIdStr);
      } catch (e) {
        resolvedThreads.delete(threadIdStr);
      }
    };
    run();
  }, [aiThreadId, human_review?.information_request, human_review?.information_answers, human_review_status, messageId]);

  useEffect(() => {
    if (human_review && human_review.steps_taken) {
      setStepsTaken(human_review.steps_taken);
    }
  }, [human_review]);

  // Update reviewed state when human_review_status changes
  useEffect(() => {
    const isReviewed = human_review_status === "approved" || human_review_status === "canceled" || human_review_status === "scheduled";
    setReviewed(isReviewed);
    if (isReviewed) {
      setReviewAction(
        human_review_status === "approved"
          ? "CONTINUE"
          : human_review_status === "scheduled"
          ? "SCHEDULED"
          : "CANCEL"
      );
    }
  }, [human_review_status]);

  // Feedback handlers
  const handleCopyMessage = async () => {
    try {
      // Extract text content from the message
      const textContent = typeof content === 'string' ? content : 
        (content as any)?.props?.children || '';
      
      // If it's a React element with dangerouslySetInnerHTML, extract the HTML content
      let textToCopy = '';
      if (typeof content === 'object' && (content as any)?.props?.dangerouslySetInnerHTML) {
        textToCopy = (content as any).props.dangerouslySetInnerHTML.__html;
        // Strip HTML tags for plain text
        textToCopy = textToCopy.replace(/<[^>]*>/g, '');
      } else {
        textToCopy = textContent;
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setSnackbarMessage('Message copied to clipboard');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to copy message');
      setSnackbarOpen(true);
    }
  };

  const handleShareTicket = async () => {
    try {
      const ticketUrl = window.location.href;
      await navigator.clipboard.writeText(ticketUrl);
      setSnackbarMessage('Ticket link copied to clipboard');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to copy ticket link');
      setSnackbarOpen(true);
    }
  };

  const handleThumbsUp = () => {
    setFeedbackType('thumbs_up');
    setFeedbackModalOpen(true);
  };

  const handleThumbsDown = () => {
    setFeedbackType('thumbs_down');
    setFeedbackModalOpen(true);
  };

  // Format conversation history for feedback metadata (only up to the current message)
  const formatConversationHistory = () => {
    if (!messages || messages.length === 0) {
      return 'No conversation history available';
    }

    // Find the index of the current message
    const currentEntryId = message?.entry_id;
    const currentMessageIndex = messages.findIndex((msg: any) =>
      currentEntryId ? msg.entry_id === currentEntryId : msg.message_id === messageId
    );
    
    // Only include messages up to and including the current message
    const messagesUpToCurrent = currentMessageIndex >= 0 
      ? messages.slice(0, currentMessageIndex + 1)
      : messages; // Fallback to all messages if current message not found

    const formattedMessages = messagesUpToCurrent.map((msg: any, index: number) => {
      const timestamp = new Date(parseInt(msg.created_at?.seconds || '0') * 1000).toLocaleString();
      const sender = msg.user_id === currentUser?.email ? 'User' : 'Assistant';
      
      let content = msg.content;
      
      // Handle human review messages
      if (msg.human_review) {
        const firstTool = msg.human_review.tools && msg.human_review.tools[0];
        content = firstTool ? `[Tool Action: ${firstTool.tool_name}] ${firstTool.tool_description || 'No description'}` : '[Tool Action]';
        if (msg.human_review_status) {
          content += ` (Status: ${msg.human_review_status})`;
        }
      }
      
      if (msg.human_review_status === "approved" || msg.human_review_status === "canceled" || msg.human_review_status === "scheduled") {
        const action =
          msg.human_review_status === "canceled"
            ? "canceled"
            : msg.human_review_status === "scheduled"
            ? "scheduled"
            : "approved";
        content = `[Action: ${action}]`;
      }
      
      // Truncate very long messages
      if (content.length > 500) {
        content = content.substring(0, 500) + '... [truncated]';
      }
      
      return `${index + 1}. [${timestamp}] ${sender}: ${content}`;
    });

    return formattedMessages.join('\n\n');
  };

  const handleFeedbackSubmit = async () => {
    try {
      // Prepare feedback data
      const conversationHistory = formatConversationHistory();
      const currentEntryId = message?.entry_id;
      const currentMessageIndex = messages?.findIndex((msg: any) =>
        currentEntryId ? msg.entry_id === currentEntryId : msg.message_id === messageId
      ) || -1;
      const messagesUpToCurrent = currentMessageIndex >= 0 ? messages?.slice(0, currentMessageIndex + 1) : messages;
      
      const feedbackData = {
        type: (feedbackType === 'thumbs_up' ? 'positive' : 'negative') as 'positive' | 'negative',
        ticketId: ticketId || 'unknown',
        url: window.location.href,
        comment: feedbackText,
        context: {
          messageId: messageId,
          ticketId: ticketId,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          totalMessages: messagesUpToCurrent?.length || 0,
          aiThreadId: aiThreadId,
          conversationHistory: conversationHistory
        }
      };

      // Submit feedback to API
      await dispatch(SupervisorAction.submitFeedback(feedbackData));
      
      setFeedbackModalOpen(false);
      setFeedbackText('');
      setFeedbackType(null);
      setSnackbarMessage('Feedback submitted successfully');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to submit feedback. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleFeedbackCancel = () => {
    setFeedbackModalOpen(false);
    setFeedbackText('');
    setFeedbackType(null);
  };

  const MAX_CONTENT_HEIGHT = 320;

  useLayoutEffect(() => {
    setIsTruncatable(false);
  }, [content]);

  const rawMessageContent = String((message as any)?.content ?? "");
  const isAssistantStreaming = Boolean((message as any)?.isStreaming);

  useLayoutEffect(() => {
    const root = contentRef.current;
    if (!root || sender !== "assistant") return;
    if (messageBlocksTablePdfDownloads(rawMessageContent)) return;
    if (isAssistantStreaming) return;
    return mountTablePdfDownloadLinksBelowTables(root);
  }, [content, sender, rawMessageContent, isAssistantStreaming]);

  const [, setOriginalArgTypes] = useState<Record<string, 'object' | 'primitive'>>({});

  const [editedArgs, setEditedArgs] = useState<Record<number, Record<string, any>>>(() => {
    // Don't initialize if human_review is not available
    if (!human_review || !human_review.tools || human_review.tools.length === 0) {
      return {};
    }

    if (human_review.tools && human_review.tools.length > 0) {
      const parsed: Record<number, Record<string, any>> = {};
      const argTypes: Record<string, 'object' | 'primitive'> = {};

      // Process each tool's args
      human_review.tools.forEach((tool: any, toolIndex: number) => {
        const argsJson = tool.args_json;
        if (argsJson && argsJson !== "{}") {
          let toolParsed: Record<string, any> = {};

          if (typeof argsJson === "string") {
            toolParsed = parseNestedArgs(argsJson);
          } else if (typeof argsJson === "object") {
            const unwrapped = parseNestedArgs(argsJson);
            Object.entries(unwrapped).forEach(([key, value]) => {
              if (value && typeof value === "object" && "argValue" in value) {
                const normalizedValue = value as any;
                toolParsed[key] = normalizedValue.argValue;
              } else {
                toolParsed[key] = value;
              }
            });
          }

          parsed[toolIndex] = toolParsed;
          Object.entries(toolParsed).forEach(([key, value]) => {
            argTypes[key] = (value !== null && typeof value === "object") ? 'object' : 'primitive';
          });
        }
      });

      setOriginalArgTypes(argTypes);
      return parsed;
    }
    return {};
  });

  const initializedCallIdRef = useRef<string | null>(null);
  
  useEffect(() => {
    if (!human_review || !human_review.tools || human_review.tools.length === 0) {
      return;
    }
    
    const currentCallId = human_review.tools[0]?.call_id || null;

    if (initializedCallIdRef.current === currentCallId && Object.keys(editedArgs).length > 0) {
      return;
    }
    
    initializedCallIdRef.current = currentCallId;
    const parsed: Record<number, Record<string, any>> = {};
    const argTypes: Record<string, 'object' | 'primitive'> = {};

    human_review.tools.forEach((tool: any, toolIndex: number) => {
      const argsJson = tool.args_json;

      const isNormalized = (human_review as any)?.args_normalized === true;

      if (argsJson && argsJson !== "{}") {
        let toolParsed: Record<string, any> = {};

        if (typeof argsJson === "string") {
          toolParsed = parseNestedArgs(argsJson);
        } else if (typeof argsJson === "object") {
          const unwrapped = parseNestedArgs(argsJson);
          if (isNormalized) {
            Object.entries(unwrapped).forEach(([key, value]) => {
              toolParsed[key] = value;
            });
          } else {
            Object.entries(unwrapped).forEach(([key, value]) => {
              if (value && typeof value === "object" && "argValue" in value) {
                const normalizedValue = value as any;
                toolParsed[key] = normalizedValue.argValue;
              } else {
                toolParsed[key] = value;
              }
            });
          }
        }

        parsed[toolIndex] = toolParsed;
        Object.entries(toolParsed).forEach(([key, value]) => {
          argTypes[key] = (value !== null && typeof value === "object") ? 'object' : 'primitive';
        });
      }
    });

    setOriginalArgTypes(argTypes);
    setEditedArgs(parsed);
    // Skip editedArgs: this effect seeds the form once per HITL call_id, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [human_review, reviewed]);


  const extractUserEdits = (editedArgs: Record<number, Record<string, any>>, selectedTools: Set<number>): Record<number, Record<string, any>> => {
    try {
      // keep full selected tool args for UPDATE/CONTINUE payloads; cortex expects complete tool input shape
      const userEdits: Record<number, Record<string, any>> = {};
      Object.entries(editedArgs).forEach(([toolIndexStr, toolArgs]) => {
        const toolIndex = parseInt(toolIndexStr);
        if (selectedTools.has(toolIndex)) {
          userEdits[toolIndex] = {};
          Object.entries(toolArgs).forEach(([key, value]) => {
            if (value !== null && typeof value === "object" && !Array.isArray(value)) {
              userEdits[toolIndex][key] = JSON.parse(JSON.stringify(value));
            } else {
              userEdits[toolIndex][key] = value;
            }
          });
        }
      });
      return userEdits;
    } catch {
      return {};
    }
  };

  const isSubmittingRef = useRef<boolean>(false);

  const buildReviewResponseData = (
    action: "CONTINUE" | "CANCEL" | "UPDATE" | "SCHEDULED",
    answerOverride?: Record<string, string>,
  ) => {
    const userEdits =
      action === "CONTINUE" || action === "UPDATE" || action === "SCHEDULED"
        ? extractUserEdits(editedArgs, selectedTools)
        : {};
    const responseData: any = { userEdits };
    if (hasTools) {
      responseData.selectedTools = Array.from(selectedTools);
    }
    if (hasPlans) {
      responseData.selectedPlans = Array.from(selectedPlans);
    }
    if (hasInformationRequests) {
    // Overridden by an action pause's button answer. The answer cannot be written into state first
    // and submitted in the same click -- the handler would read the value it had before the click --
    // so it is threaded through instead.
      responseData.informationResponses = { ...informationAnswers, ...(answerOverride || {}) };
    }
    return responseData;
  };

  const selectedToolsHaveScheduleRefs = React.useCallback(() => {
    const tools = Array.isArray(human_review?.tools) ? human_review?.tools : [];
    return Array.from(selectedTools).some((toolIndex) =>
      containsScheduleReference(getToolArgs(tools?.[toolIndex], editedArgs, toolIndex))
    );
  }, [editedArgs, human_review?.tools, selectedTools]);

  const openScheduleDialog = async () => {
    if (!scheduleActionEnabled) return;
    if (reviewed || isSubmittingRef.current || !hasTools || selectedTools.size === 0) {
      return;
    }
    const defaultTimezone = getDefaultTimeZone();
    const firstTool = human_review?.tools?.[Array.from(selectedTools)[0]];
    setScheduleMode("new");
    setSelectedScheduleId("");
    setScheduleTitle(firstTool?.tool_description || firstTool?.tool_name || "Scheduled action");
    setScheduleDescription("");
    setScheduleTimezone(defaultTimezone);
    setScheduleFor(toDateTimeLocalInTimeZone(null, defaultTimezone));
    setScheduleError(null);
    try {
      const scheduleApi = new ScheduleApi();
      const response = await scheduleApi.listSchedules();
      const candidates = (response.schedules || []).filter((schedule) =>
        schedule.status === "scheduled" || schedule.status === "failed"
      );
      setAvailableSchedules(candidates);
      setSelectedScheduleId(candidates.length > 0 ? String(candidates[0].id || "") : "");
      setScheduleDialogOpen(true);
    } catch (error: any) {
      setScheduleError(error?.message || "Failed to load schedules");
      setScheduleDialogOpen(true);
    }
  };

  const submitSchedule = async () => {
    if (!scheduleActionEnabled) {
      setScheduleError("Scheduling HITL actions is not enabled for this workspace");
      return;
    }
    if (!aiThreadId || !currentUser?.email) {
      setScheduleError("Missing thread or user context");
      return;
    }
    if (selectedTools.size === 0) {
      setScheduleError("Select at least one action to schedule");
      return;
    }
    if (scheduleMode === "new" && (!scheduleTitle.trim() || !scheduleFor)) {
      setScheduleError("Title and run time are required");
      return;
    }
    setScheduleSubmitting(true);
    setScheduleError(null);
    try {
      const responseData = buildReviewResponseData("SCHEDULED");
      const scheduleApi = new ScheduleApi();
      const scheduleResponse = await scheduleApi.scheduleHitlActions({
        schedule_id: scheduleMode === "existing" ? selectedScheduleId : null,
        schedule:
          scheduleMode === "new"
            ? {
                title: scheduleTitle.trim(),
                description: scheduleDescription.trim(),
                scheduled_for: fromDateTimeLocalInTimeZone(scheduleFor, scheduleTimezone),
                timezone: scheduleTimezone,
              }
            : null,
        ai_thread_id: aiThreadId,
        customer_id: propCustomerId || null,
        message_id: messageId || null,
        human_review: {
          action: "SCHEDULED",
          ...responseData,
        },
      });
      const linkedScheduleId = scheduleResponse?.schedule?.id || (scheduleMode === "existing" ? selectedScheduleId : null);
      if (linkedScheduleId) {
        responseData.schedule_id = String(linkedScheduleId);
        responseData.schedule_title = scheduleResponse?.schedule?.title || scheduleTitle.trim() || null;
        setScheduledScheduleId(String(linkedScheduleId));
      }

      setReviewAction("SCHEDULED");
      setReviewed(true);
      isSubmittingRef.current = true;
      try {
        if (onReviewAction) {
          await onReviewAction("SCHEDULED", JSON.stringify(responseData), messageId);
        } else {
          await dispatch(
            ChatAction.handleHumanReview(
              aiThreadId,
              currentUser.email,
              "SCHEDULED",
              JSON.stringify(responseData),
              messageId
            )
          );
        }
      } catch {
        const createdSchedule = scheduleResponse?.schedule;
        if (createdSchedule?.id) {
          setScheduleMode("existing");
          setSelectedScheduleId(String(createdSchedule.id));
          setAvailableSchedules((prev) => {
            const exists = prev.some((schedule) => String(schedule.id) === String(createdSchedule.id));
            return exists ? prev : [createdSchedule, ...prev];
          });
        }
        throw new Error(
          "Action was added to the schedule, but Theo could not record the scheduled response. Try again with the existing schedule selected."
        );
      }
      setScheduleDialogOpen(false);
    } catch (error: any) {
      setReviewed(false);
      setReviewAction(null);
      const message = getChatSendErrorMessage(error);
      setScheduleError(message);
      if (!onReviewAction) {
        dispatchChatSendFailure(dispatch, error);
      }
      if (onReviewError) {
        onReviewError(message);
      }
    } finally {
      isSubmittingRef.current = false;
      setScheduleSubmitting(false);
    }
  };
  
  const handleReviewAction = (
    action: "CONTINUE" | "CANCEL" | "UPDATE",
    answerOverride?: Record<string, string>,
  ) => {
    if (reviewed || isSubmittingRef.current) {
      return;
    }

    if (!hasTools && !hasPlans && !hasInformationRequests) {
      return;
    }

    if ((action === "CONTINUE" || action === "UPDATE") && selectedToolsHaveScheduleRefs()) {
      return;
    }

    if (action === "CONTINUE") {
      const hasSelectedTools = selectedTools.size > 0;
      const hasSelectedPlans = selectedPlans.size > 0;
      const hasInformationAnswers = Object.keys(informationAnswers).length > 0 && 
        Object.values(informationAnswers).some((ans: any) => ans && ans.trim().length > 0);

      // An action pause is answered by its button: the answer arrives as an override, not
      // through the typed-answer state, so there is nothing here to require.
      if (!hasSelectedTools && !hasSelectedPlans && !hasInformationAnswers && !isActionPause) {
        return;
      }

      // Validate all information request fields before submission. An action pause carries its
      // own answer (PauseCard gates its field), so an inferred field type has nothing to check.
      if (hasInformationRequests && human_review?.information_request && !isActionPause) {
        const errors: string[] = [];
        const newValidationErrors: Record<string, string> = {};
        const newTouchedFields: Record<string, boolean> = {};
        
        human_review.information_request.forEach((req: any) => {
          const value = informationAnswers[req.id] || "";
          
          // Detect if field is required
          const isRequired =
            String(req.id || "").startsWith("structured_workflow_resolution:") ||
            req.question.toLowerCase().includes("(required)");
          
          const fieldType = inferInformationRequestFieldType(req.id, req.question);
          
          const error = validateField(value, fieldType, isRequired);
          if (error) {
            // Extract field name from question (before first colon)
            const fieldName = req.question.split(':')[0].trim();
            errors.push(`${fieldName}: ${error}`);
            newValidationErrors[req.id] = error;
            newTouchedFields[req.id] = true;
          }
        });

        if (errors.length > 0) {
          setFormErrors(errors);
          setValidationErrors(newValidationErrors);
          setTouchedFields(prev => ({ ...prev, ...newTouchedFields }));
          return; // Don't submit if there are validation errors
        }
        
        // Clear form errors if validation passes
        setFormErrors([]);
      }
    }
    
    try {
      setReviewAction(action);
      setReviewed(true);
      isSubmittingRef.current = true;
      
      // Build response data - only include the HITL type that exists
      const responseData = buildReviewResponseData(action, answerOverride);
      
      const sendRequest = async () => {
        try {
          if (onReviewAction) {
            await onReviewAction(action, JSON.stringify(responseData), messageId);
          } else {
            await dispatch(
            ChatAction.handleHumanReview(
              aiThreadId,
              currentUser?.email,
              action,
              JSON.stringify(responseData),
              messageId
            )
          );
        }
          
        } finally {
          isSubmittingRef.current = false;
        }
      };
      
      sendRequest().catch((error) => {
        setReviewed(false);
        setReviewAction(null);
        isSubmittingRef.current = false;
        if (!onReviewAction) {
          dispatchChatSendFailure(dispatch, error);
        }
        const message = getChatSendErrorMessage(error);
        if (onReviewError) {
          onReviewError(message);
        } else {
          setSnackbarMessage(message);
          setSnackbarOpen(true);
        }
      });
    } catch {
      setReviewed(false);
      setReviewAction(null);
      isSubmittingRef.current = false;
    }
  };

  const handleAccordionChange = (toolIndex: number) => {
    setExpandedTools((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(toolIndex)) {
        newSet.delete(toolIndex);
      } else {
        newSet.add(toolIndex);
      }
      return newSet;
    });
  };

  const handleCheckboxChange = (
    toolIndex: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    hasUserInteracted.current = true; // Mark that user has interacted
    
    setSelectedTools((prev) => {
      const newSet = new Set(prev);
      if (event.target.checked) {
        newSet.add(toolIndex);
      } else {
        newSet.delete(toolIndex);
      }
      return newSet;
    });
  };

  const handlePlanCheckboxChange = (
    planIndex: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    hasUserInteracted.current = true; // Mark that user has interacted
    
    setSelectedPlans((prev) => {
      const newSet = new Set(prev);
      if (event.target.checked) {
        // For plans, only allow one selection at a time
        newSet.clear();
        newSet.add(planIndex);
      } else {
        newSet.delete(planIndex);
      }
      return newSet;
    });
  };

  const DisplayValue = {
    displayName: "Display Name",
    mailNickname: "Mail Nickname",
    description: "Description",
    mailEnabled: "Mail Enabled",
    securityEnabled: "Security Enabled",
    group_display_name: "Group Display Name",
    group_id: "Group ID",
    user_id: "User ID",
    group_mail_nickname: "Group Mail Nickname",
  };

  const extractTablePdfDownloadPayload = (
    table: HTMLTableElement,
    _tableIndex: number
  ): { title: string; subtitle: string; headers: string[]; rows: string[][] } | null => {
    const allRows = Array.from(table.querySelectorAll("tr")) as HTMLTableRowElement[];
    if (allRows.length === 0) return null;

    const cleanHeaderText = (cell: HTMLTableCellElement) => (cell.textContent || "").trim();

    const headerRow =
      allRows.find((row) => row.querySelectorAll("th").length > 0) || null;
    const headerCells = headerRow
      ? (Array.from(headerRow.querySelectorAll("th")) as HTMLTableCellElement[])
      : [];
    let headers = headerCells.map(cleanHeaderText).filter((value) => value.length > 0);

    const bodyRows = allRows.filter((row) => row !== headerRow);
    let dataBodyRows = bodyRows;
    if (!headers.length && bodyRows.length > 0) {
      const firstTds = Array.from(bodyRows[0].querySelectorAll("td")) as HTMLTableCellElement[];
      headers = firstTds.map(cleanHeaderText).filter((value) => value.length > 0);
      dataBodyRows = bodyRows.slice(1);
    }

    const dataRows = dataBodyRows
      .map((row) =>
        (Array.from(row.querySelectorAll("td")) as HTMLTableCellElement[]).map((td) =>
          serializeTableCellForPdfDownload(td)
        )
      )
      .filter((row) => row.some((value) => value.length > 0));

    if (!headers.length && !dataRows.length) return null;
    return {
      title: TABLE_PDF_DEFAULT_DOCUMENT_TITLE,
      subtitle: getPdfSubtitleBeforeTable(table),
      headers,
      rows: dataRows,
    };
  };

  const handleTablePdfDownload = (tableIndex: number) => {
    const threadId = String(message?.ai_thread_id ?? "").trim() || null;
    if (!threadId) {
      setSnackbarMessage("PDF download unavailable: missing thread context");
      setSnackbarOpen(true);
      return;
    }
    const container = contentRef.current;
    if (!container) {
      setSnackbarMessage("PDF download unavailable: table content not ready");
      setSnackbarOpen(true);
      return;
    }
    const tables = Array.from(container.querySelectorAll("table")) as HTMLTableElement[];
    const targetTable = tables[tableIndex];
    if (!targetTable) {
      setSnackbarMessage("PDF download unavailable: selected table not found");
      setSnackbarOpen(true);
      return;
    }
    const tablePayload = extractTablePdfDownloadPayload(targetTable, tableIndex);
    if (!tablePayload) {
      setSnackbarMessage("Unable to parse table for PDF download.");
      setSnackbarOpen(true);
      return;
    }

    setTablePdfLinkLoadingState(container, tableIndex, true);
    const api = new ChatApi();
    void api
      .downloadTableAsPdf(
        threadId,
        String(message?.message_id ?? "").trim() || null,
        tablePayload.title,
        tablePayload.headers,
        tablePayload.rows,
        tablePayload.subtitle
      )
      .then((res) => {
        if (res?.url) {
          window.open(res.url, "_blank", "noopener,noreferrer");
        }
        setSnackbarMessage("PDF downloaded successfully.");
        setSnackbarOpen(true);
      })
      .catch((error: any) => {
        setSnackbarMessage(error?.message || "Failed to download table PDF.");
        setSnackbarOpen(true);
      })
      .finally(() => {
        setTablePdfLinkLoadingState(container, tableIndex, false);
      });
  };

  const handleExportLinkClick = (event: React.MouseEvent<HTMLElement>) => {
    const nativeEvent = event.nativeEvent as MouseEvent;
    const path = typeof nativeEvent.composedPath === "function" ? nativeEvent.composedPath() : [];
    const pathAnchor = path.find((node) => node instanceof HTMLAnchorElement) as HTMLAnchorElement | undefined;
    const target = event.target as Element | null;
    const closestAnchor =
      target && typeof target.closest === "function"
        ? (target.closest("a") as HTMLAnchorElement | null)
        : null;
    const anchor = pathAnchor || closestAnchor;
    if (!anchor) return;

    if (anchor.hasAttribute("data-neo-table-pdf-download")) {
      event.preventDefault();
      event.stopPropagation();
      const idx = Number.parseInt(anchor.getAttribute("data-neo-table-pdf-download") || "0", 10);
      void handleTablePdfDownload(idx);
      return;
    }

    const href = anchor.getAttribute("href") || "";
    if (isCsvExportHref(href)) {
      event.preventDefault();
      try {
        const exportUrl = new URL(anchor.href, window.location.origin);
        const threadId = String(message?.ai_thread_id ?? "").trim() || null;
        const parsed = parseCsvExportSearchParams(exportUrl.searchParams);
        if (!threadId || !parsed) {
          setSnackbarMessage("CSV download unavailable: missing thread or invalid link.");
          setSnackbarOpen(true);
          return;
        }
        void downloadThreadCsvExport(threadId, parsed.exportId, parsed.filename).catch((error: any) => {
          setSnackbarMessage(error?.message || "Failed to download CSV.");
          setSnackbarOpen(true);
        });
      } catch {
        setSnackbarMessage("Invalid CSV download link.");
        setSnackbarOpen(true);
      }
      return;
    }

    if (isPdfExportHref(href)) {
      event.preventDefault();
      try {
        const exportUrl = new URL(anchor.href, window.location.origin);
        const threadId = String(message?.ai_thread_id ?? "").trim() || null;
        const parsed = parsePdfExportSearchParams(exportUrl.searchParams);
        if (!threadId || !parsed) {
          setSnackbarMessage("PDF download unavailable: missing thread or invalid link.");
          setSnackbarOpen(true);
          return;
        }
        void downloadThreadPdfExport(threadId, parsed.exportId, parsed.filename).catch((error: any) => {
          setSnackbarMessage(error?.message || "Failed to download PDF.");
          setSnackbarOpen(true);
        });
      } catch {
        setSnackbarMessage("Invalid PDF download link.");
        setSnackbarOpen(true);
      }
      return;
    }

    if (!href.includes("/export_results")) return;

    event.preventDefault();
    try {
      const exportUrl = new URL(anchor.href, window.location.origin);
      const parsed = parseExportParams(exportUrl.searchParams.get("params") || "");
      const threadId = String(message?.ai_thread_id ?? "").trim() || null;
      if (!parsed) {
        setSnackbarMessage("Invalid export link payload.");
        setSnackbarOpen(true);
        return;
      }
      if (!threadId) {
        setSnackbarMessage("Export unavailable: missing thread context");
        setSnackbarOpen(true);
        return;
      }
      const userEmail = String(currentUser?.email ?? "").trim();
      if (!userEmail) {
        setSnackbarMessage("Export unavailable: missing user context");
        setSnackbarOpen(true);
        return;
      }

      const requestId = `exp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const exportLabel = `Export ${parsed.actionName}`;
      dispatch({
        type: ActionTypes.ADD_MESSAGE,
        payload: {
          message_id: `temp_${requestId}`,
          ai_thread_id: threadId,
          user_id: userEmail,
          content: exportLabel,
          content_type: "EXPORT_REQUEST",
          created_at: { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
          pending: true,
          requestId,
        },
      });
      dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
      dispatch({ type: ActionTypes.START_THINKING });

      const api = new ChatApi();
      void api
        .handleExportAction(threadId, userEmail, parsed.actionName, parsed.paramsObj || {}, requestId)
        .catch((error: any) => {
          setSnackbarMessage(error?.message || "Failed to start export.");
          setSnackbarOpen(true);
        });
    } catch {
      setSnackbarMessage("Invalid export link.");
      setSnackbarOpen(true);
    }
  };

  return (
    <Box display="flex" alignItems="flex-start" mb={0.5}>
      <Box
        sx={{
          mt: 0.5,
          mr: 0.5,
          width: 24,
          minWidth: 24,
          flex: "0 0 auto",
        }}
      >
        <Avatar
          sx={{
            bgcolor: sender === "assistant" ? "#EFEDFF" : "#DDF6F1",
            width: 24,
            height: 24,
          }}
        >
          {sender === "assistant" ? (
            <img src={AssistantIcon} alt="Assistant" />
          ) : (
            userId[0]
          )}
        </Avatar>
      </Box>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          p: 0.75,
          borderRadius: "6px",
          maxWidth: "100%",
          minWidth: "130px",
          // Adjust font size for embedded mode to match ConnectWise
          fontSize: `${fontSizes.body}px`,
          "& p, & div, & span, & *": {
            fontSize: `${fontSizes.body}px`, // Ensure all text content uses correct size
          },
          "&:hover .chat-feedback-tray, &:focus-within .chat-feedback-tray": {
            opacity: 1,
          },
        }}
      >
        <Box display="flex" alignItems="center" mb={0.5}>
          <Typography
            variant="subtitle2"
            fontWeight={500}
            fontFamily={"var(--theo-font-sans)"}
            fontSize={fontSizes.body}
            color="#172033"
          >
            {userId}
          </Typography>
          <Box mx={1} width="1px" height={16} bgcolor="divider" />
          <Typography
            variant="caption"
            fontWeight={500}
            fontFamily={"var(--theo-font-sans)"}
            fontSize={fontSizes.small}
            color="#64748B"
          >
            {timestamp}
          </Typography>
          {automationModeBadge ? (
            <Box sx={{ ml: 1, display: "flex", alignItems: "center", minWidth: 0 }}>
              {automationModeBadge}
            </Box>
          ) : null}
          {headerInlineContent ? (
            <Box sx={{ ml: 1, display: "flex", alignItems: "center", minWidth: 0 }}>
              {headerInlineContent}
            </Box>
          ) : null}
          {status === 'confirmed' && (
            <CheckCircleIcon 
              sx={{ 
                ml: 1, 
                fontSize: fontSizes.small, 
                color: '#4CAF50' 
              }} 
            />
          )}
          {status === 'failed' && (
            <CloseIconMui 
              sx={{ 
                ml: 1, 
                fontSize: fontSizes.small, 
                color: '#F44336' 
              }} 
            />
          )}
          {sender === "assistant" && (
            <Box
              className="chat-feedback-tray"
              sx={{
                ml: "auto",
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                pl: 1,
                opacity: 0,
                transition: "opacity 0.15s ease-in-out",
                "@media (hover: none)": { opacity: 1 },
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
              }}
            >
              <Tooltip title="Copy message">
                <MuiIconButton
                  size="small"
                  aria-label="Copy message"
                  onClick={handleCopyMessage}
                  sx={{
                    color: "#666F8D",
                    padding: "4px",
                    "&:hover": { color: "#1976d2" },
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  <ContentCopyIcon />
                </MuiIconButton>
              </Tooltip>
              <Tooltip title="Share ticket">
                <MuiIconButton
                  size="small"
                  aria-label="Share ticket"
                  onClick={handleShareTicket}
                  sx={{
                    color: "#666F8D",
                    padding: "4px",
                    "&:hover": { color: "#1976d2" },
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  <ShareIcon />
                </MuiIconButton>
              </Tooltip>
              <Tooltip title="Thumbs up">
                <MuiIconButton
                  size="small"
                  aria-label="Thumbs up"
                  onClick={handleThumbsUp}
                  sx={{
                    color: "#666F8D",
                    padding: "4px",
                    "&:hover": { color: "#4caf50" },
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  <ThumbUpOutlinedIcon />
                </MuiIconButton>
              </Tooltip>
              <Tooltip title="Thumbs down">
                <MuiIconButton
                  size="small"
                  aria-label="Thumbs down"
                  onClick={handleThumbsDown}
                  sx={{
                    color: "#666F8D",
                    padding: "4px",
                    "&:hover": { color: "#f44336" },
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  <ThumbDownOutlinedIcon />
                </MuiIconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        {human_review && human_review.tools && human_review.tools.length > 0 && 
         (!human_review.plans || human_review.plans.length === 0) && (
          <Box mt={2} mb={2}>
            {showStepsTaken && (
            <Accordion
              sx={{
                border: `1px solid ${NEUTRAL.border}`,
                borderRadius: `${RADIUS.card}px`,
                boxShadow: "none",
                // mb: 1, // Bottom margin
                backgroundColor: "#FFF6ED",
                "& .MuiBox-root": {
                  backgroundColor: "#FFF6ED",
                },
                "& .MuiAccordionSummary-content": {
                  margin: "0 !important",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "0 !important",
                },
                "& .MuiAccordionSummary-root": {
                  minHeight: "auto !important",
                  padding: "4px 12px !important",
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ fontSize: 18, color: NEUTRAL.textSecondary }} />
                }
              >
                <Typography
                  fontSize={fontSizes.body}
                  color="#000000"
                  fontWeight={500}
                  fontFamily="var(--theo-font-sans)"
                >
                  Steps Taken
                </Typography>
              </AccordionSummary>
              {stepsTaken.length > 0 ? (
                <AccordionDetails
                  sx={{
                    borderTop: "1px solid #E0E0E0",
                    p: 2,
                  }}
                >
                  <Box display="flex" flexDirection="column" p={1}>
                    {stepsTaken.map((step, stepIndex) => (
                      <Box
                        key={`step-${stepIndex}`}
                        flexDirection="row"
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 16, color: "#4CAF50" }}
                        />
                        <Typography>{step}</Typography>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              ) : (
                <AccordionDetails
                  sx={{
                    borderTop: "1px solid #E0E0E0",
                    p: 2,
                  }}
                >
                  <Typography p={1}>No steps taken</Typography>
                </AccordionDetails>
              )}
            </Accordion>
            )}

            {human_review.tools && human_review.tools.map((tool: any, toolIndex: number) => {
              const toolName = tool.tool_name || "";
              const toolDescription = formatHitlToolDescription(
                toolName,
                tool.tool_description || ""
              );
              let argsJson = tool.args_json;
              let resolvedArgs = tool.resolved_args;

              const parseResolvedArgs = (raw: unknown): Record<string, unknown> => {
                if (raw == null) return {};
                if (typeof raw === "object" && !Array.isArray(raw)) return raw as Record<string, unknown>;
                if (typeof raw === "string") {
                  try {
                    const parsed = JSON.parse(raw);
                    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed : {};
                  } catch {
                    return {};
                  }
                }
                return {};
              };
              
              resolvedArgs = parseResolvedArgs(resolvedArgs);

              if (!argsJson || argsJson === "{}") return null;
              
              if (typeof argsJson === "string") {
                try {
                  argsJson = JSON.parse(argsJson);
                } catch {
                  argsJson = {};
                }
              }

              const scheduledAction = tool.call_id ? scheduledActionByCallId.get(String(tool.call_id)) : null;
              const rawToolAction = Array.isArray(human_review?.tool_action)
                ? human_review.tool_action[toolIndex]
                : human_review?.tool_action ?? tool.action ?? tool.tool_action;
              const isScheduledTool =
                Boolean(scheduledAction) ||
                String(rawToolAction || "").toUpperCase() === "SCHEDULED" ||
                human_review_status === "scheduled";
              const showUpdatedArgs = Boolean(isHistory || reviewed);
              const updatedArgPaths = showUpdatedArgs
                ? normalizeUpdatedArgPaths(tool.updated_arg_paths)
                : [];
              const originalArgs =
                tool.original_args_json &&
                typeof tool.original_args_json === "object" &&
                !Array.isArray(tool.original_args_json)
                  ? (tool.original_args_json as Record<string, unknown>)
                  : null;

              return (
                  <Accordion
                    key={toolIndex}
                    expanded={expandedTools.has(toolIndex)}
                    onChange={() => handleAccordionChange(toolIndex)}
                    disableGutters
                    classes={{
                      root: "custom-accordion-root",
                      rounded: "custom-accordion-rounded",
                    }}
                    sx={{
                      backgroundColor: CARD_BACKGROUND,
                      boxShadow: CARD_EDGE,
                      border: `1px solid ${NEUTRAL.border}`,
                      borderRadius: `${RADIUS.card}px !important`,
                      overflow: "hidden",
                      boxSizing: "border-box",
                      mb: 1,
                      "&:before": { display: "none" },
                      "& .MuiBox-root": {
                        backgroundColor: "transparent",
                      },
                      "&.Mui-expanded": {
                        margin: "0 0 8px 0",
                      },
                      "&.MuiPaper-root": {
                        backgroundColor: CARD_BACKGROUND,
                        boxShadow: CARD_EDGE,
                        backgroundImage: "none",
                      },
                      "&.MuiPaper-rounded, &.MuiAccordion-rounded": {
                        borderRadius: `${RADIUS.card}px`,
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{ fontSize: isEmbedded ? 18 : 22, color: NEUTRAL.textSecondary }}
                        />
                      }
                      sx={{
                        backgroundColor: CARD_BACKGROUND,
                        p: isEmbedded ? 0.5 : 0,
                        pl: isEmbedded ? 1 : 1.25,
                        minHeight: isEmbedded ? 40 : 46,
                        borderBottom: expandedTools.has(toolIndex) ? `1px solid ${NEUTRAL.border}` : "none",
                        "& .MuiAccordionSummary-content": {
                          my: isEmbedded ? 0.5 : 1,
                        },
                        "& .MuiAccordionSummary-root": {
                          boxShadow: "none",
                        },
                        "& .MuiAccordionDetails-root": {
                          boxShadow: "none",
                        },
                        "& .MuiPaper-root": {
                          boxShadow: "none",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        <Checkbox
                          checked={selectedTools.has(toolIndex)}
                          disabled={isHistory || reviewed}
                          onChange={(event) =>
                            handleCheckboxChange(toolIndex, event)
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          sx={{
                            p: isEmbedded ? 0.25 : 0.5,
                            mr: isEmbedded ? 0.5 : 1,
                            color: NEUTRAL.borderStrong,
                            "&.Mui-checked": {
                              color: VIOLET.main,
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: isEmbedded ? 18 : 22,
                            },
                          }}
                        />
                        {toolDescription && (
                          <Typography
                            fontSize={fontSizes.body}
                            fontWeight={600}
                            color={NEUTRAL.text}
                            fontFamily={"var(--theo-font-sans)"}
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            {toolDescription}
                          </Typography>
                        )}
                        {updatedArgPaths.length > 0 ? (
                          <HitlUpdatedFieldsChip compact={isEmbedded} />
                        ) : null}
                        {isScheduledTool ? renderScheduleViewIconLink() : null}
                        {scheduledAction ? (
                          <Chip
                            size="small"
                            label={normalizeScheduledActionStatus(scheduledAction.status)}
                            color={scheduleStatusColor(scheduledAction.status) as any}
                            sx={{
                              flexShrink: 0,
                              ml: 1,
                              fontFamily: "var(--theo-font-sans)",
                              fontSize: isEmbedded ? 11 : 12,
                              fontWeight: 700,
                              height: isEmbedded ? 22 : 24,
                            }}
                          />
                        ) : null}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        p: isEmbedded ? 1 : 2,
                        pl: isEmbedded ? 1.5 : 2.5,
                        backgroundColor: CARD_BACKGROUND,
                      }}
                    >
                      <HitlActionParamsEditor
                        args={editedArgs[toolIndex] && Object.keys(editedArgs[toolIndex]).length > 0 ? editedArgs[toolIndex] : argsJson}
                        argTypes={tool.arg_types_json || {}}
                        resolvedArgs={resolvedArgs || {}}
                        disabled={isHistory || reviewed}
                        onChange={(nextArgs) => {
                          if (!isHistory && !reviewed) {
                            setEditedArgs((prev) => ({ ...prev, [toolIndex]: nextArgs }));
                          }
                        }}
                        fontSizes={fontSizes}
                        displayLabelMap={DisplayValue}
                        groupEmptyValues
                        updatedArgPaths={updatedArgPaths}
                        originalArgs={originalArgs}
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </Box>
        )}

        {/* Plans HITL Section */}
        {human_review && human_review.plans && human_review.plans.length > 0 && (
          <Box
            mt={2}
            mb={2}
            sx={{
              background: "linear-gradient(135deg, #CFBFF8 0%, #DDE2FC 100%)",
              p: 1,
              borderRadius: 3,
              border: "1px solid #F0F2F5",
              boxShadow: "0px 2px 4px 0px rgba(25, 33, 61, 0.08)",
            }}
          >
            <Typography
              fontSize={16}
              fontWeight={600}
              color="#000000"
              fontFamily={"var(--theo-font-sans)"}
              mb={2}
            >
              {"Select a Plan"}
            </Typography>

            {human_review.plans && human_review.plans.map((plan: any, planIndex: number) => {
              const planName = plan.plan_name || "";
              const planDescription = plan.plan_description || "";
              const planSteps: string[] = Array.isArray(plan.steps) ? plan.steps : [];
              const planUrl = plan.url || "";

              // Determine which plan was actually selected
              const selectedPlanName = (human_review as any)?.selected_plan_name;
              const isSelectedPlan = selectedPlanName && planName === selectedPlanName;

              return (
                  <Accordion
                    key={planIndex}
                    expanded={expandedTools.has(planIndex)}
                    onChange={() => handleAccordionChange(planIndex)}
                    sx={{
                      border: "none",
                      borderRadius: "4px",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "& .MuiBox-root": {
                        backgroundColor: "transparent",
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: "0 !important",
                      },
                      "& .MuiAccordionDetails-root": {
                        padding: "0 !important",
                      },
                      "& .MuiAccordionSummary-root": {
                        minHeight: "auto !important",
                        padding: "4px 12px !important",
                      },
                      "&.MuiPaper-root": {
                        boxShadow: "none",
                        backgroundImage: "none",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{
                            fontSize: fontSizes.small,
                            color: "#ffffff",
                            backgroundColor: "#CFBFF8",
                            borderRadius: "50%",
                            width: "12px",
                            height: "12px",
                          }}
                        />
                      }
                      sx={{
                        backgroundColor: "transparent",
                        border: "none",
                        p: 0,
                        "& .MuiAccordionSummary-root": {
                          boxShadow: "none",
                        },
                        "& .MuiAccordionDetails-root": {
                          boxShadow: "none",
                        },
                        "& .MuiPaper-root": {
                          boxShadow: "none",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        <Checkbox
                          checked={isSelectedPlan || selectedPlans.has(planIndex)}
                          disabled={
                            isSelectedPlan ||
                            isHistory ||
                            reviewed
                          }
                          onChange={(event) =>
                            handlePlanCheckboxChange(planIndex, event)
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          sx={{
                            p: 0.5,
                            mr: 1,
                            "&.Mui-checked": {
                              color: "#8062D9",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: 24,
                              borderRadius: "4px",
                            },
                          }}
                        />
                        {planDescription && (
                          <Typography
                            fontSize={fontSizes.body}
                            fontWeight={600}
                            color="#000000"
                            fontFamily={"var(--theo-font-sans)"}
                            sx={{ flex: 1 }}
                          >
                            {planDescription}
                          </Typography>
                        )}
                        {planUrl && (
                          <LinkIcon
                            sx={{
                              fontSize: 16,
                              color: "#CFBFF8",
                              cursor: "pointer",
                              ml: 1,
                              "&:hover": {
                                color: "#9B7EDE",
                              },
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              window.open(planUrl, "_blank", "noopener,noreferrer");
                            }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        borderTop: "1px solid #E0E0E0",
                        p: 2,
                      }}
                    >
                      {/* Plan Steps */}
                      {planSteps.length > 0 && (
                        <Box display="flex" flexDirection="column" p={1}>
                          {planSteps.map((step: string, stepIndex: number) => {
                            return (
                              <Box
                                key={`step-${stepIndex}`}
                                flexDirection="row"
                                display="flex"
                                alignItems="flex-start"
                                gap={1}
                                py={0.75}
                              >
                                <RadioButtonUncheckedIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "#9E9E9E",
                                    mt: 0.25,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    flex: 1,
                                  }}
                                >
                                  {step}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </Box>
        )}

        {/* Show human_review_ack (e.g. "You approved this plan.") below plan selection, before plan messages */}
        {human_review?.plans && human_review.plans.length > 0 &&
         (message as any)?.human_review_ack && (
          <Box mt={1}>
            <Typography fontSize={fontSizes.body} color="#6B7280" fontFamily="var(--theo-font-sans)" sx={{ fontStyle: "italic" }}>
              {formatHumanReviewAck((message as any).human_review_ack, currentUser)}
            </Typography>
          </Box>
        )}

        {/* Plan steps are now part of regular AI message content, no separate display needed */}

        {/* Action pause: one card, rendered entirely from its card_json. Its buttons are the answer
            and go through handleReviewAction like every other HITL control. */}
        {human_review && isActionPause && (
          <Box
            mt={2}
            mb={2}
            sx={{
              backgroundColor: "#FFFFFF",
              p: 1,
              borderRadius: 3,
              border: "1px solid #D9E0EA",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
            }}
          >
            <PauseCard
              humanReview={human_review}
              status={human_review_status}
              reviewed={reviewed}
              showControls={!isHistory || isLastMessage || reviewed || ["approved", "canceled", "scheduled"].includes(String(human_review_status || ""))}
              onAnswer={(action, answers) => handleReviewAction(action, answers)}
              appearance="panel"
              compact={isEmbedded}
              bodyFontSize={fontSizes.body}
            />
          </Box>
        )}

        {/* Information Requests HITL Section */}
        {human_review && !isActionPause && human_review.information_request && human_review.information_request.length > 0 && (
          <Box
            mt={2}
            mb={2}
            sx={{
              backgroundColor: "#FFFFFF",
              p: 1,
              borderRadius: 3,
              border: "1px solid #D9E0EA",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
            }}
          >
            {/* Title row with inline Post button */}
            <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
              <Typography
                fontSize={16}
                fontWeight={600}
                color="#172033"
                fontFamily={"var(--theo-font-sans)"}
              >
                {isTechnicianChecklistFormType
                  ? "Technician checklist"
                  : isWorkflowResolutionFormType
                  ? "Workflow step needs information"
                  : "Information Required"}
              </Typography>
              {ticketId && !isWorkflowFormType && !isTechnicianChecklistFormType && !isWorkflowResolutionFormType && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    if (!ticketId) return;
                    setPostingAllIR(true);
                    try {
                      const config = await import('../config.js');
                      const baseURL = config.default.apiUrl || window.location.origin;
                      const accessToken =
                        (await getWebAuthToken().catch(() => null)) ||
                        store.getState()?.auth?.accessToken ||
                        '';
                      const infoReqs = (human_review?.information_request || []) as Array<{id: string; question: string}>;
                      const ids = infoReqs.map(r => r.id);
                      const content = infoReqs.map(r => `- [IR:${r.id}] ${r.question}`).join('\n');
                      const response = await fetch(`${baseURL}/tickets/${ticketId}/notes`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${accessToken}`,
                        },
                        body: JSON.stringify({
                          noteTitle: 'Information Request',
                          noteContent: content,
                          isPublic: true,
                          infoReqIds: ids,
                          aiThreadId: aiThreadId || '',
                        }),
                      });
                      if (!response.ok) {
                        const err = await response.json();
                        throw new Error(err.error || 'Failed to post');
                      }
                      setSnackbarMessage('Information request posted');
                      setSnackbarOpen(true);
                      setPostedAllIR(true);
                    } catch (e: any) {
                      setSnackbarMessage('Failed to post: ' + (e?.message || ''));
                      setSnackbarOpen(true);
                    } finally {
                      setPostingAllIR(false);
                    }
                  }}
                  disabled={
                    (human_review_status === "approved" ||
                      human_review_status === "canceled" ||
                      human_review_status === "scheduled") ||
                    (isHistory && !isLastMessage) ||
                    reviewed ||
                    !!((human_review as any)?.information_answers && Object.keys((human_review as any).information_answers).length > 0) ||
                    postingAllIR || postedAllIR
                  }
                  sx={{ textTransform: 'none', ml: 2 }}
                >
                  {postingAllIR ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      Posting...
                    </Box>
                  ) : postedAllIR ? 'Posted' : 'Post'}
                </Button>
              )}
            </Box>

            {/* Form-level validation errors */}
            {formErrors.length > 0 && (
              <Box 
                mb={2} 
                p={2} 
                sx={{ 
                  backgroundColor: '#FEF2F2', 
                  border: '1px solid #FCA5A5',
                  borderRadius: '4px'
                }}
              >
                <Typography                 fontSize={fontSizes.body} fontWeight={600} mb={1} color="#DC2626" fontFamily="var(--theo-font-sans)">
                  Please fix the following errors:
                </Typography>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 2.5 }}>
                  {formErrors.map((error, idx) => (
                    <li key={idx}>
                      <Typography fontSize={fontSizes.small} color="#DC2626" fontFamily="var(--theo-font-sans)">
                        {error}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}

            {isTechnicianChecklistFormType ? (
              <TechnicianChecklistReview
                requests={human_review.information_request}
                answers={informationAnswers}
                onAnswersChange={setInformationAnswers}
                disabled={
                  human_review_status === "approved" ||
                  human_review_status === "canceled" ||
                  human_review_status === "scheduled" ||
                  (isHistory && !isLastMessage) ||
                  reviewed
                }
              />
            ) : isWorkflowResolutionFormType ? (
              <WorkflowStepResolutionReview
                requests={human_review.information_request}
                answers={informationAnswers}
                onAnswersChange={setInformationAnswers}
                disabled={
                  human_review_status === "approved" ||
                  human_review_status === "canceled" ||
                  human_review_status === "scheduled" ||
                  (isHistory && !isLastMessage) ||
                  reviewed
                }
              />
            ) : human_review.information_request.map((infoReq: any, index: number) => (
              <Box key={infoReq.id} mb={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography
                  fontSize={fontSizes.body}
                  fontWeight={500}
                  color="#666666"
                  fontFamily={"var(--theo-font-sans)"}
                    sx={{ flex: 1 }}
                >
                  {infoReq.question}
                </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={(() => {
                    const raw = informationAnswers[infoReq.id] || "";
                    return raw
                      .replace(/<br\s*\/?>/gi, "\n")
                      .replace(/\\n/g, "\n")
                      .replace(/\r\n/g, "\n")
                      .replace(/\r/g, "\n");
                  })()}
                  multiline={(() => {
                    const raw = informationAnswers[infoReq.id] || "";
                    const normalized = raw
                      .replace(/<br\s*\/?>/gi, "\n")
                      .replace(/\\n/g, "\n")
                      .replace(/\r\n/g, "\n")
                      .replace(/\r/g, "\n");
                    const lineCount = normalized ? normalized.split("\n").length : 1;
                    return lineCount > 1 || normalized.length > 120;
                  })()}
                  minRows={(() => {
                    const raw = informationAnswers[infoReq.id] || "";
                    const normalized = raw
                      .replace(/<br\s*\/?>/gi, "\n")
                      .replace(/\\n/g, "\n")
                      .replace(/\r\n/g, "\n")
                      .replace(/\r/g, "\n");
                    const lineCount = normalized ? normalized.split("\n").length : 1;
                    if (lineCount <= 1 && normalized.length <= 120) return undefined;
                    const baseRows = lineCount > 1 ? lineCount : 4;
                    return Math.min(6, baseRows);
                  })()}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setInformationAnswers(prev => ({
                      ...prev,
                      [infoReq.id]: newValue
                    }));
                    const isRequired =
                      String(infoReq.id || "").startsWith("structured_workflow_resolution:") ||
                      infoReq.question.toLowerCase().includes("(required)");
                    const fieldType = inferInformationRequestFieldType(
                      infoReq.id,
                      infoReq.question
                    );
                    const error = validateField(newValue, fieldType, isRequired);
                    setValidationErrors(prev => ({
                      ...prev,
                      [infoReq.id]: error || ""
                    }));
                  }}
                  onBlur={(e) => {
                    const newValue = e.target.value;
                    
                    // Mark field as touched
                    setTouchedFields(prev => ({
                      ...prev,
                      [infoReq.id]: true
                    }));
                    
                    // Detect if field is required
                    const isRequired =
                      String(infoReq.id || "").startsWith("structured_workflow_resolution:") ||
                      infoReq.question.toLowerCase().includes("(required)");
                    
                    const fieldType = inferInformationRequestFieldType(
                      infoReq.id,
                      infoReq.question
                    );
                    
                    const error = validateField(newValue, fieldType, isRequired);
                    setValidationErrors(prev => ({
                      ...prev,
                      [infoReq.id]: error || ""
                    }));
                  }}
                  placeholder="Enter your answer..."
                  disabled={
                    (human_review_status === "approved" ||
                      human_review_status === "canceled" ||
                      human_review_status === "scheduled") ||
                    (isHistory && !isLastMessage) ||
                    reviewed
                  }
                  error={!!validationErrors[infoReq.id] && touchedFields[infoReq.id]}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: `${fontSizes.small}px`,
                      fontFamily: "var(--theo-font-sans)",
                    },
                  }}
                />
                {validationErrors[infoReq.id] && touchedFields[infoReq.id] && (
                  <Typography 
                    color="error" 
                    fontSize={fontSizes.small} 
                    mt={0.5}
                    fontFamily="var(--theo-font-sans)"
                  >
                    {validationErrors[infoReq.id]}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Stack direction="row" spacing={isEmbedded ? 1.5 : 3} sx={{ mt: isEmbedded ? 0.5 : 1 }}>
          {(() => {
            // An action pause carries its own buttons (PauseCard above).
            if (isActionPause) return null;
            const hasSelectedPlan = (human_review as any)?.selected_plan_name;
            const hasPlanRequest = human_review?.plans && human_review.plans.length > 0;
            
            // Check per-tool actions from tool_action array (for multiple tools with different actions)
            const toolActions = human_review?.tool_action 
              ? (Array.isArray(human_review.tool_action) ? human_review.tool_action : [human_review.tool_action])
              : [];
            
            // Check if any tool has CONTINUE/UPDATE action (check all tools, not just selected ones)
            // This handles both active review (selectedTools) and history (all toolActions)
            const hasContinueAction = toolActions.length > 0 && toolActions.some(action => {
              return action === "CONTINUE" || action === "UPDATE";
            });
            
            // Check if any tool has CANCEL action (check all tools, not just selected ones)
            const hasCancelAction = toolActions.length > 0 && toolActions.some(action => {
              return action === "CANCEL";
            });
            const hasScheduledAction = toolActions.length > 0 && toolActions.some(action => {
              return action === "SCHEDULED";
            });
            
            // Combine server state (human_review_status) and local state (reviewed/reviewAction)
            // for seamless UX: local state gives instant feedback, server state persists across renders
            const isServerApproved = human_review_status === "approved";
            const isServerCanceled = human_review_status === "canceled";
            const isServerScheduled = human_review_status === "scheduled";
            const wasApproved = isServerApproved || reviewAction === "CONTINUE" || reviewAction === "UPDATE" || hasContinueAction;
            const wasCanceled = isServerCanceled || reviewAction === "CANCEL" || hasCancelAction;
            const wasScheduled = isServerScheduled || reviewAction === "SCHEDULED" || hasScheduledAction;
            const anyReviewed = wasApproved || wasCanceled || wasScheduled;
            
            // Show Cancel whenever the action is not approved: before review (so user can cancel even with selections) or after cancel
            const shouldShowCancel = !wasApproved && !wasScheduled;
            
            // Only hide plan-selection buttons before review
            const shouldHideButtons = !anyReviewed && (hasPlanRequest && hasSelectedPlan);

            const approvedCallIds = (human_review?.tools ?? [])
              .filter((_t: { call_id?: string }, i: number) =>
                isHitlApprovalAction(
                  Array.isArray(human_review?.tool_action)
                    ? human_review!.tool_action[i]
                    : human_review?.tool_action
                )
              )
              .map((t: { call_id?: string }) => t.call_id)
              .filter((id: string | undefined): id is string => Boolean(id));

            const hasUndoableApproved = (human_review?.tools ?? []).some(
              (t: { call_id?: string; is_undoable?: boolean }, i: number) => {
                const isApproved = isHitlApprovalAction(
                  Array.isArray(human_review?.tool_action)
                    ? human_review!.tool_action[i]
                    : human_review?.tool_action
                );
                return isApproved && t.is_undoable === true;
              }
            );

            const hrTools = human_review?.tools;
            const sendEmailOnlyHitl =
              Boolean(hrTools?.length) &&
              hrTools!.every((t: { tool_name?: string }) => ["send_email", "compose_email"].includes(t.tool_name || ""));

            // Welcome-letter (or other phase-generated) PDF surfaced by Cortex for this HITL.
            let hitlPdfExport: { downloadUrl: string; filename: string; title: string } | null = null;
            if (sendEmailOnlyHitl) {
              let rawPdfExport: unknown =
                (human_review as any)?.pdf_export_json ??
                (human_review as any)?.pdfExportJson;
              if (rawPdfExport && typeof rawPdfExport === "object" && !Array.isArray(rawPdfExport)) {
                const wrappedValue = (rawPdfExport as { value?: unknown }).value;
                if (wrappedValue !== undefined) rawPdfExport = wrappedValue;
              }
              if (typeof rawPdfExport === "string" && rawPdfExport.trim()) {
                try {
                  rawPdfExport = JSON.parse(rawPdfExport);
                } catch {
                  rawPdfExport = null;
                }
              }
              if (rawPdfExport && typeof rawPdfExport === "object" && !Array.isArray(rawPdfExport)) {
                const pdfObj = rawPdfExport as Record<string, unknown>;
                const downloadUrl = String(pdfObj.download_url ?? "").trim();
                if (downloadUrl) {
                  hitlPdfExport = {
                    downloadUrl,
                    filename: String(pdfObj.filename ?? "").trim() || "welcome-letter.pdf",
                    title: String(pdfObj.title ?? "").trim() || "Welcome letter",
                  };
                }
              }
            }
            const handleHitlPdfDownload = async () => {
              if (!hitlPdfExport || hitlPdfDownloading) return;
              setHitlPdfDownloading(true);
              try {
                const exportUrl = new URL(hitlPdfExport.downloadUrl, window.location.origin);
                const parsed = parsePdfExportSearchParams(exportUrl.searchParams);
                const threadId = String(message?.ai_thread_id ?? "").trim() || null;
                if (!threadId || !parsed) {
                  setSnackbarMessage("PDF download unavailable: missing thread or invalid link.");
                  setSnackbarOpen(true);
                  return;
                }
                await downloadThreadPdfExport(threadId, parsed.exportId, parsed.filename);
              } catch (error: any) {
                setSnackbarMessage(error?.message || "Failed to download PDF.");
                setSnackbarOpen(true);
              } finally {
                setHitlPdfDownloading(false);
              }
            };

            let firstSendEmailLinks: {
              href: string;
              owaUrl: string;
              truncated: boolean;
              owaRecipientsUnsupported: boolean;
              owaCcPromotedToTo: boolean;
            } | null = null;
            if (sendEmailOnlyHitl && hrTools) {
              const idx = hrTools.findIndex((t: { tool_name?: string }) => ["send_email", "compose_email"].includes(t.tool_name || ""));
              if (idx >= 0) {
                let argsObj: unknown = hrTools[idx].args_json;
                if (typeof argsObj === "string") {
                  try {
                    argsObj = JSON.parse(argsObj);
                  } catch {
                    argsObj = {};
                  }
                }
                if (argsObj && typeof argsObj === "object" && !Array.isArray(argsObj)) {
                  const merged = mergeM365SendEmailInput(
                    argsObj as Record<string, unknown>,
                    editedArgs[idx]
                  );
                  if (merged) {
                    const { href, truncated: truncatedMailto } = buildMailtoFromSendEmailInput(merged);
                    const {
                      url: owaUrl,
                      truncated: truncatedOwa,
                      unsupportedRecipients: owaRecipientsUnsupported,
                      ccPromotedToTo: owaCcPromotedToTo,
                    } =
                      buildOutlookWebComposeFromSendEmailInput(merged);
                    if (href || owaUrl || owaRecipientsUnsupported) {
                      firstSendEmailLinks = {
                        href: href || "",
                        owaUrl: owaUrl || "",
                        truncated: truncatedMailto || truncatedOwa,
                        owaRecipientsUnsupported,
                        owaCcPromotedToTo,
                      };
                    }
                  }
                }
              }
            }

            const selectedToolsContainScheduleRefs = selectedToolsHaveScheduleRefs();
            const hasCurrentInformationValidationErrors = (human_review?.information_request ?? []).some(
              (req: any) => {
                const value = informationAnswers[req.id] || "";
                const isRequired =
                  String(req.id || "").startsWith("structured_workflow_resolution:") ||
                  req.question.toLowerCase().includes("(required)");
                const fieldType = inferInformationRequestFieldType(req.id, req.question);
                return Boolean(validateField(value, fieldType, isRequired));
              }
            );
            const reviewDisabledBase =
              anyReviewed ||
              (human_review?.tools && human_review.tools.length > 0 && selectedTools.size === 0) ||
              hasCurrentInformationValidationErrors;
            const scheduleDisabledBase =
              !scheduleActionEnabled || reviewDisabledBase;
            const continueDisabledBase =
              reviewDisabledBase ||
              selectedToolsContainScheduleRefs ||
              (human_review?.plans && human_review.plans.length > 0 && selectedPlans.size === 0);

            const approvalButtonOptions = { compact: Boolean(isEmbedded), fontSize: fontSizes.body };
            const continueButtonSx = approvalPrimaryButtonSx(approvalButtonOptions);
            const scheduleButtonSx = approvalSecondaryButtonSx(approvalButtonOptions);
            const emailLaunchButtonSx = approvalLinkButtonSx(approvalButtonOptions);
            return (
              <>
                {/* Send-email HITL: the caption + mail-launch links PERSIST regardless of review state
                    (they stay after Cancel as well as Continue). Only the Continue/Cancel action row is gated. */}
                {human_review && (!isHistory || isLastMessage || anyReviewed) && !shouldHideButtons && sendEmailOnlyHitl && (
                  <Stack direction="column" spacing={1.25} sx={{ alignItems: "flex-start", width: "100%", maxWidth: 760 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#57534E", fontFamily: "var(--theo-font-sans)", lineHeight: 1.5 }}
                    >
                      Download the welcome letter first, then open the email draft and attach it before sending.
                    </Typography>
                    {hitlPdfExport ? (
                      <Box sx={{ width: "100%" }}>
                        <Button
                          onClick={handleHitlPdfDownload}
                          variant="outlined"
                          size="small"
                          disabled={hitlPdfDownloading}
                          startIcon={
                            hitlPdfDownloading
                              ? <CircularProgress size={16} color="inherit" />
                              : undefined
                          }
                          title={`Download ${hitlPdfExport.title} and attach it to the email before sending`}
                          sx={emailLaunchButtonSx}
                        >
                          {hitlPdfDownloading
                            ? "Downloading welcome letter PDF…"
                            : "1. Download welcome letter PDF"}
                        </Button>
                      </Box>
                    ) : null}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.25,
                        width: "100%",
                      }}
                    >
                      {firstSendEmailLinks?.href ? (
                        <Button
                          component="a"
                          href={firstSendEmailLinks.href}
                          variant="outlined"
                          size="small"
                          rel="noopener noreferrer"
                          sx={emailLaunchButtonSx}
                        >
                          {hitlPdfExport ? "2. Open in default mail app" : "Open in default mail app"}
                        </Button>
                      ) : null}
                      {firstSendEmailLinks?.owaUrl ? (
                        <Button
                          component="a"
                          href={firstSendEmailLinks!.owaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          size="small"
                          sx={emailLaunchButtonSx}
                        >
                          {hitlPdfExport ? "2. Outlook on the web" : "Outlook on the web"}
                        </Button>
                      ) : null}
                    </Box>
                    {firstSendEmailLinks?.owaRecipientsUnsupported ? (
                      <Typography variant="caption" sx={{ color: "#8A4B08", fontFamily: "var(--theo-font-sans)", lineHeight: 1.5 }}>
                        Outlook on the web compose links cannot prefill BCC recipients. Use “Open in default mail app”
                        to preserve BCC.
                      </Typography>
                    ) : null}
                    {firstSendEmailLinks?.owaCcPromotedToTo ? (
                      <Typography variant="caption" sx={{ color: "#8A4B08", fontFamily: "var(--theo-font-sans)", lineHeight: 1.5 }}>
                        Outlook on the web does not support CC in compose links, so configured CC recipients will appear
                        in the To field.
                      </Typography>
                    ) : null}
                    {/* Actions are gated: they disappear once the HITL is resolved (Cancel) but the links above stay. */}
                    {(!anyReviewed || wasApproved) && (
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={1}
                        alignItems="center"
                        sx={{
                          width: "100%",
                          pt: 1.25,
                          mt: 0.25,
                          borderTop: "1px solid #E7E5E4",
                        }}
                      >
                        {!selectedToolsContainScheduleRefs ? (
                          <Button
                            variant="contained"
                            startIcon={<CheckIcon sx={{ fontSize: isEmbedded ? 16 : 20 }} />}
                            disabled={continueDisabledBase}
                            onClick={() => handleReviewAction("CONTINUE")}
                            sx={continueButtonSx}
                          >
                            {hitlPdfExport
                              ? "I've attached the PDF and sent the email — continue"
                              : "I've sent the email — continue"}
                          </Button>
                        ) : null}
                        {shouldShowCancel ? (
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon sx={{ fontSize: isEmbedded ? 16 : 20 }} />}
                            disabled={anyReviewed}
                            onClick={() => handleReviewAction("CANCEL")}
                            sx={scheduleButtonSx}
                          >
                            Cancel
                          </Button>
                        ) : null}
                        {scheduleActionEnabled && hasTools && !anyReviewed ? (
                          <Button
                            variant="outlined"
                            startIcon={<ScheduleIcon sx={{ fontSize: isEmbedded ? 16 : 20 }} />}
                            disabled={scheduleDisabledBase}
                            onClick={openScheduleDialog}
                            sx={scheduleButtonSx}
                          >
                            Schedule
                          </Button>
                        ) : null}
                      </Stack>
                    )}
                    {firstSendEmailLinks?.truncated ? (
                      <Typography variant="caption" sx={{ color: "#888", fontFamily: "var(--theo-font-sans)" }}>
                        Some link text was shortened to fit URL length limits.
                      </Typography>
                    ) : null}
                  </Stack>
                )}
                {/* Non-send-email HITLs: normal Continue (hidden once resolved). */}
                {human_review && (!isHistory || isLastMessage || anyReviewed) && !shouldHideButtons && (!anyReviewed || wasApproved) && !sendEmailOnlyHitl && !selectedToolsContainScheduleRefs && (
                  <Button
                    variant="contained"
                    startIcon={<CheckIcon sx={{ fontSize: isEmbedded ? 16 : 20 }} />}
                    disabled={continueDisabledBase}
                    onClick={() => handleReviewAction("CONTINUE")}
                    sx={continueButtonSx}
                  >
                    {hitlUsesBrowser(human_review) ? "Continue in a browser" : "Continue"}
                  </Button>
                )}
                {/* Cancel — send-email-only HITL renders Cancel next to "I've sent" (above); otherwise neutral outline here */}
                {human_review &&
                  (!isHistory || isLastMessage || anyReviewed) &&
                  !shouldHideButtons &&
                  shouldShowCancel &&
                  !sendEmailOnlyHitl && (
                    <Button
                      variant="outlined"
                      startIcon={<CloseIcon sx={{ fontSize: isEmbedded ? 16 : 18 }} />}
                      disabled={anyReviewed}
                      onClick={() => handleReviewAction("CANCEL")}
                      sx={scheduleButtonSx}
                    >
                      {"Cancel"}
                    </Button>
                  )}
                {scheduleActionEnabled && human_review && hasTools && (!isHistory || isLastMessage) && !shouldHideButtons && !anyReviewed && !sendEmailOnlyHitl && (
                  <Button
                    variant="outlined"
                    startIcon={<ScheduleIcon sx={{ fontSize: isEmbedded ? 16 : 20 }} />}
                    disabled={scheduleDisabledBase}
                    onClick={openScheduleDialog}
                    sx={scheduleButtonSx}
                  >
                    Schedule
                  </Button>
                )}
                {/* Only render where the surface wired an undo handler (ticket thread, chat assistant) — not observer/extension.
                    Gate on !isLastMessage so the button waits until the agent has produced its follow-up response:
                    wasApproved flips optimistically on Continue-click, but the tool may still be executing. Once a
                    subsequent message exists this bubble is no longer last, which means the action has completed.
                    Also gate on hasUndoableApproved: hide when none of the approved actions support undo. */}
                {wasApproved && approvedCallIds.length > 0 && onOpenUndoModal && !isLastMessage && hasUndoableApproved && (
                  <Button
                    variant="outlined"
                    startIcon={<UndoIcon />}
                    onClick={() => onOpenUndoModal(approvedCallIds)}
                    sx={{
                      minHeight: isEmbedded ? 32 : 41,
                      fontSize: `${fontSizes.body}px`,
                      p: isEmbedded ? "6px 12px !important" : "10px 16px !important",
                      textTransform: "none",
                      fontFamily: "var(--theo-font-sans)",
                      fontWeight: 700,
                      borderColor: "#9CA3AF",
                      color: "#374151",
                      "&:hover": {
                        borderColor: "#6B7280",
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    Undo
                  </Button>
                )}
              </>
            );
          })()}
        </Stack>
        {/* Show human_review_ack for tools/information requests (not plans - plans show it above) */}
        {human_review && (message as any)?.human_review_ack &&
         (!human_review?.plans || human_review.plans.length === 0) && (
          <Box mt={1}>
            <Typography fontSize={fontSizes.medium} color="#6B7280" fontFamily="var(--theo-font-sans)">
              {formatHumanReviewAck((message as any).human_review_ack, currentUser)}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Box
            ref={contentRef}
            sx={{
              width: "100%",
              position: "relative",
              maxHeight: isTruncatable ? `${MAX_CONTENT_HEIGHT}px` : "none",
              overflowY: isTruncatable ? "auto" : "visible",
              overflowX: "hidden",
              scrollbarWidth: "thin",
              scrollbarColor: "#D1D5DB transparent",
              ...messageContentWrapSx,
              "&::after": isTruncatable
                ? {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "70px",
                    background:
                      "linear-gradient(to bottom, rgba(255, 255, 255, 0), white 85%)",
                    pointerEvents: "none",
                  }
                : {},
            }}
          >
            <Box
              sx={{
                width: "100%",
                background: "#fff",
                overflowX: "hidden",
                display: workflowTimerScheduleStatus || workflowTimerScheduleId ? "flex" : "block",
                alignItems: workflowTimerScheduleStatus || workflowTimerScheduleId ? "center" : undefined,
                gap: workflowTimerScheduleStatus || workflowTimerScheduleId ? 1 : undefined,
                flexWrap: workflowTimerScheduleStatus || workflowTimerScheduleId ? "wrap" : undefined,
                ...messageContentWrapSx,
              }}
            >
              <Typography
                component="div"
                fontSize={fontSizes.subheader}
                fontWeight={400}
                fontFamily={"var(--theo-font-sans)"}
                color={"#666F8D"}
                variant="body1"
                sx={{
                  fontSize: `${fontSizes.subheader}px !important`,
                  ...messageContentWrapSx,
                  // Preserve intentional typography inside rich cards (for example,
                  // the 14px investigation-summary hierarchy) while retaining the
                  // wrapping safeguards for generated content.
                  "& *": {
                    minWidth: 0,
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  },
                  "& p": {
                    marginTop: "0.3em",
                    marginBottom: "0.3em",
                  },
                  "& ol, & ul": {
                    marginTop: "0.3em",
                    marginBottom: "0.3em",
                    paddingLeft: "1.75em",
                    // Markdown list items contain block paragraphs. Keeping markers
                    // outside lets the marker and the first line share one row.
                    listStylePosition: "outside",
                  },
                  "& li": {
                    marginBottom: "0.15em",
                  },
                  "& li > p": {
                    margin: 0,
                  },
                // Treat inline code (usually IDs, email addresses, and short values) as
                // supporting text rather than a full monospace code snippet. Browser
                // defaults make these tokens look disproportionately large and introduce
                // a second typeface into ordinary assistant prose.
                "& code": {
                  fontFamily: "inherit",
                  fontSize: "0.94em",
                  fontWeight: 500,
                  lineHeight: "inherit",
                  color: "inherit",
                  backgroundColor: "rgba(102, 111, 141, 0.08)",
                  border: "1px solid rgba(102, 111, 141, 0.12)",
                  borderRadius: "3px",
                  padding: "0.05em 0.25em",
                },
                // Preserve a distinct, compact monospace treatment for fenced code
                // blocks while keeping it visually contained in the chat column.
                "& pre": {
                  margin: "0.5em 0",
                  padding: "0.75em",
                  overflowX: "auto",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: "0.9em",
                  lineHeight: 1.5,
                  backgroundColor: "rgba(102, 111, 141, 0.08)",
                  border: "1px solid rgba(102, 111, 141, 0.12)",
                  borderRadius: "6px",
                },
                "& pre code": {
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  fontWeight: "inherit",
                  backgroundColor: "transparent",
                  border: 0,
                  borderRadius: 0,
                  padding: 0,
                },
                  "& table": {
                    width: "100%",
                    maxWidth: "100%",
                    tableLayout: "fixed",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    margin: 0,
                    fontSize: `${fontSizes.small}px`,
                    background: "transparent",
                  },
                  // Chat markdown tables: keep header + body left-aligned. GFM ---: would
                  // otherwise right-align body cells while CSS left-aligns headers (shifted look).
                  "& thead th": {
                    background: "#F4F5FF",
                    color: "#7C8493",
                    fontWeight: 500,
                    fontSize: `${fontSizes.small}px`,
                    padding: "12px 14px",
                    border: "none",
                    textAlign: "left",
                    fontFamily: "var(--theo-font-sans)",
                    letterSpacing: 0,
                  },
                  "& tbody td": {
                    background: "#fff",
                    color: "#666F8D",
                    fontWeight: 500,
                    fontSize: `${fontSizes.small}px`,
                    padding: "14px 12px",
                    border: "none",
                    textAlign: "left",
                    fontFamily: "var(--theo-font-sans)",
                    letterSpacing: 0,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  },
                  "& tbody tr.selected td": {
                    background: "#F5F3FF",
                    fontWeight: 600,
                    color: "#282B3A",
                  },
                  "& tbody tr:hover td": {
                    background: "#F9F9FB",
                  },
                  "& tbody tr:last-of-type td": {
                    borderBottom: "none",
                  },
                  ...(workflowTimerScheduleStatus
                    ? {
                        display: "inline-flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        width: "auto",
                        maxWidth: "100%",
                        "& p": {
                          display: "inline",
                          marginTop: 0,
                          marginBottom: 0,
                          maxWidth: "100%",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        },
                      }
                    : {}),
                }}
                onClick={handleExportLinkClick}
              >
                {shouldRenderMessageContent ? content : null}
              </Typography>
              {workflowTimerScheduleStatus ? (
                <Chip
                  size="small"
                  label={workflowTimerScheduleStatus.label}
                  color={workflowTimerScheduleStatus.color}
                  sx={{ height: 22, fontFamily: "var(--theo-font-sans)", fontWeight: 700, flexShrink: 0 }}
                />
              ) : null}
              {renderWorkflowTimerScheduleActions()}
            </Box>
          </Box>
          {isTruncatable && (
            <Button
              onClick={() => setModalOpen(true)}
              sx={{
                mt: 1,
                color: "#9B7AEF",
                textTransform: "none",
                fontWeight: 500,
                fontSize: `${fontSizes.body}px`,
                fontFamily: "var(--theo-font-sans)",
                "&:hover": {
                  backgroundColor: "rgba(155, 122, 239, 0.1)",
                },
              }}
            >
              View All
            </Button>
          )}
          <Dialog
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                width: "1095px",
                maxHeight: "90vh",
                flexShrink: 0,
                borderRadius: "25px",
                background: "#FFF",
                p: 0,
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontFamily: "var(--theo-font-sans)",
                fontWeight: 600,
                fontSize: 18,
                color: "#19213D",
                p: 2,
                minHeight: 48,
              }}
            >
              <span></span>
              <MuiIconButton
                onClick={() => setModalOpen(false)}
                size="small"
                sx={{
                  position: "static",
                  background: "#F5F5F5",
                  "&:hover": { background: "#E0E0E0" },
                }}
              >
                <CloseIconMui />
              </MuiIconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                  background: "#F4F5FF",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#E0E0E0",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar:horizontal": {
                  height: "8px",
                },
                scrollbarWidth: "thin",
                scrollbarColor: "#E0E0E0 #F4F5FF",
                ...messageContentWrapSx,
              }}
            >
              <Typography
                fontSize={fontSizes.subheader}
                fontWeight={400}
                fontFamily={"var(--theo-font-sans)"}
                color={"#666F8D"}
                variant="body1"
                sx={{
                  fontSize: `${fontSizes.subheader}px !important`,
                  ...messageContentWrapSx,
                  "& *": {
                    fontSize: `${fontSizes.subheader}px !important`,
                    minWidth: 0,
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  },
                  "& p": {
                    marginTop: "0.3em",
                    marginBottom: "0.3em",
                  },
                  "& ol, & ul": {
                    marginTop: "0.3em",
                    marginBottom: "0.3em",
                    paddingLeft: "1.75em",
                    listStylePosition: "inside",
                  },
                  "& li": {
                    marginBottom: "0.15em",
                  },
                  "& li > p": {
                    margin: 0,
                  },
                  "& table": {
                    width: "100%",
                    maxWidth: "100%",
                    tableLayout: "fixed",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    margin: 0,
                    fontSize: `${fontSizes.small}px`,
                    background: "transparent",
                  },
                  // Chat markdown tables: keep header + body left-aligned. GFM ---: would
                  // otherwise right-align body cells while CSS left-aligns headers (shifted look).
                  "& thead th": {
                    background: "#F4F5FF",
                    color: "#7C8493",
                    fontWeight: 500,
                    fontSize: `${fontSizes.small}px`,
                    padding: "12px 14px",
                    border: "none",
                    textAlign: "left",
                    fontFamily: "var(--theo-font-sans)",
                    letterSpacing: 0,
                  },
                  "& tbody td": {
                    background: "#fff",
                    color: "#666F8D",
                    fontWeight: 500,
                    fontSize: `${fontSizes.small}px`,
                    padding: "14px 12px",
                    border: "none",
                    textAlign: "left",
                    fontFamily: "var(--theo-font-sans)",
                    letterSpacing: 0,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  },
                  "& tbody tr.selected td": {
                    background: "#F5F3FF",
                    fontWeight: 600,
                    color: "#282B3A",
                  },
                  "& tbody tr:hover td": {
                    background: "#F9F9FB",
                  },
                  "& tbody tr:last-of-type td": {
                    borderBottom: "none",
                  },
                }}
                onClick={handleExportLinkClick}
              >
                {shouldRenderMessageContent ? content : null}
              </Typography>
            </DialogContent>
          </Dialog>
        </Box>
      </Paper>
      
      {/* Feedback Modal */}
      <Dialog
        open={feedbackModalOpen}
        onClose={handleFeedbackCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {feedbackType === 'thumbs_up' ? '👍 Positive Feedback' : '👎 Negative Feedback'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Please provide your feedback..."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={handleFeedbackCancel}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleFeedbackSubmit}
            disabled={!feedbackText.trim()}
          >
            Submit
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={scheduleActionEnabled && scheduleDialogOpen}
        onClose={() => !scheduleSubmitting && setScheduleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>
          Schedule action
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {scheduleError ? <Alert severity="error">{scheduleError}</Alert> : null}
            <TextField
              select
              label="Schedule"
              value={scheduleMode}
              onChange={(e) => setScheduleMode(e.target.value as "existing" | "new")}
              fullWidth
            >
              <MenuItem value="new">Create new schedule</MenuItem>
              <MenuItem value="existing" disabled={availableSchedules.length === 0}>
                Add to existing schedule
              </MenuItem>
            </TextField>
            {scheduleMode === "existing" ? (
              <LayeredAutocompleteSelect
                label="Existing schedule"
                value={selectedScheduleId}
                onChange={value => setSelectedScheduleId(value)}
                fullWidth
                disableClearable
                options={availableSchedules.map(schedule => ({ value: String(schedule.id), label: schedule.title + " · " + formatDateTimeInTimeZone(schedule.scheduled_for, schedule.timezone) }))}
              />
            ) : (
              <>
                <TextField
                  label="Title"
                  value={scheduleTitle}
                  onChange={(e) => setScheduleTitle(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={scheduleDescription}
                  onChange={(e) => setScheduleDescription(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
                <TextField
                  label="Run at"
                  type="datetime-local"
                  value={scheduleFor}
                  onChange={(e) => setScheduleFor(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <Autocomplete
                  options={scheduleTimezoneOptions}
                  value={scheduleTimezone}
                  onChange={(_event, value) => setScheduleTimezone(value || getDefaultTimeZone())}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Timezone"
                      fullWidth
                    />
                  )}
                  fullWidth
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <Box sx={{ p: 2, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={() => setScheduleDialogOpen(false)} disabled={scheduleSubmitting}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={submitSchedule}
            disabled={
              scheduleSubmitting ||
              (scheduleMode === "existing" && !selectedScheduleId) ||
              (scheduleMode === "new" && (!scheduleTitle.trim() || !scheduleFor))
            }
          >
            Schedule
          </Button>
        </Box>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatBubble;
